/**
 * Bahasa Melayu translation
 */
import en, { type TranslationKeys } from './en';

type StringShape<T> = {
  [K in keyof T]: T[K] extends string ? string : StringShape<T[K]>;
};

const ms: StringShape<TranslationKeys> = {
  // ── App shell ──
  app: {
    name: 'Boox Platform',
    logo: 'B',
  },
  tabs: en.tabs,
  commonAction: {
    refresh: 'Segar semula',
    runNow: 'Jalankan sekarang',
    newTask: 'Tugas baharu',
    newLocalService: 'Servis tempatan baharu',
    create: 'Cipta',
    save: 'Simpan',
  },
  hmsMatchbox: en.hmsMatchbox,
  hmsPricing: en.hmsPricing,
  hmsRates: en.hmsRates,
  shippingAgency: {
    accounts: {
      pageTitle: 'Akaun ejen perkapalan',
      activeAccounts: 'Akaun aktif',
      creditHold: 'Sekatan kredit',
      chargeProfiles: 'Konfigurasi caj tempatan',
    },
    principalTasks: {
      queueTitle: 'Meja tugas Principal',
      queued: 'Dalam giliran',
      running: 'Sedang berjalan',
      needsOverride: 'Perlu ambil alih manual',
      empty: 'Tiada tugas integrasi principal.',
      runReady: 'Pelan pelayar berautentikasi yang boleh dijalankan telah dijana',
    },
    localServices: {
      pageTitle: 'Meja servis tempatan',
      truckingOrders: 'Pesanan trucking',
      depotOrders: 'Pesanan depot',
      backlog: 'Backlog servis belum diproses',
      revenuePlanned: 'Hasil Dirancang',
      marginPlanned: 'Margin Dirancang',
      empty: 'Tiada pesanan servis tempatan.',
      filtered: 'Ditapis mengikut booking {{bookingNo}}',
      createTitle: 'Cipta pesanan servis tempatan',
      loadingQueue: 'Memuatkan giliran koordinasi servis tempatan…',
      vendor: 'Vendor',
      container: 'Kontena',
      scheduled: 'Dijadualkan',
      revenue: 'Hasil',
      cost: 'Kos',
      margin: 'Margin',
      customerBilling: 'Bil pelanggan',
      vendorCost: 'Kos vendor',
      openArDraft: 'Buka draf AR',
      openApDraft: 'Buka draf AP',
      openServiceDesk: 'Buka meja pesanan servis',
      openDepotMoves: 'Buka pergerakan depot',
      generateArDraft: 'Jana draf AR',
      generateApDraft: 'Jana draf AP',
      arDraftReady: 'Draf AR sedia',
      apDraftReady: 'Draf AP sedia',
      postCustomerInvoice: 'Pos invois pelanggan',
      postVendorBill: 'Pos bil vendor',
      confirmReceipt: 'Sahkan terimaan',
      confirmVendorPayment: 'Sahkan bayaran vendor',
      customerInvoicePosted: 'Invois pelanggan telah dipos',
      vendorBillPosted: 'Bil vendor telah dipos',
      customerReceiptConfirmed: 'Terimaan pelanggan disahkan',
      vendorPaymentConfirmed: 'Bayaran vendor disahkan',
      settlementActionFailed: 'Gagal melengkapkan tindakan penyelesaian',
      notStarted: 'Belum bermula',
      draftReady: 'Draf sedia',
      posted: 'Dipost',
      partiallySettled: 'Separa selesai',
      settled: 'Selesai',
      voided: 'Dibatalkan',
      pending: 'Menunggu',
    },
    emailIntake: {
      pageTitle: 'Intake E-mel AI',
      subtitle: 'Tukar e-mel ejen kepada draf booking, dokumentasi, release, servis tempatan atau tugas Evergreen.',
      from: 'Daripada',
      to: 'Kepada',
      subject: 'Subjek',
      body: 'Kandungan e-mel',
      autoCreate: 'Auto cipta work item draf',
      submit: 'Proses e-mel',
      result: 'Hasil proses',
      draftKind: 'Jenis draf',
      linkedBooking: 'Booking berkait',
    },
  },
  depotModule: {
    dashboardTitle: 'Gambaran operasi Depot',
    dashboardSubtitle: 'Gate, yard, EIR, inspection, move dan billing dalam satu meja kerja.',
  },
  socModule: {
    dashboardTitle: 'Menara kawalan kontena SOC',
    dashboardSubtitle: 'Lihat pool, release, overdue return dan pendedahan caj dalam satu tempat.',
  },

  // ── Login ──
  login: {
    title: 'Boox Platform',
    subtitle: 'Log masuk untuk teruskan',
    email: 'E-mel',
    loginName: 'Nama Log Masuk',
    password: 'Kata laluan',
    submit: 'Log Masuk',
    submitting: 'Log masuk…',
    failed: 'Log masuk gagal',
    heroTitle: 'Sistem Operasi Perusahaan Pintar',
    heroDesc: 'Automatikkan operasi, jejak tempahan, dan urus keseluruhan aliran kerja operasi anda di satu tempat.',
    feature1: 'Penjejakan Masa Nyata',
    feature2: 'Automasi Pintar',
    feature3: 'Sokongan Berbilang Penyewa',
    welcomeBack: 'Selamat kembali',
    forgotPassword: 'Lupa kata laluan?',
    securityNote: 'Sambungan anda disulitkan dan selamat.',
    back: 'Kembali',
    selectTenant: 'Pilih ruang kerja',
    brandingLoading: 'Penjenamaan tenant masih dimuatkan. Sila cuba sebentar lagi.',
    secureAccessFor: 'Akses selamat untuk {{company}}',
    workspaceOnHost: '{{portal}} di {{host}}',
    tenantWorkspace: 'Ruang Kerja Tenant',
    shortAccountHint: 'Akaun pendek disokong di sini, contohnya {{hint}}',
    dedicatedLogin: 'Log masuk ini dikhaskan untuk {{company}}.',
    unauthorizedTenant: 'Anda tidak dibenarkan mengakses aplikasi ini.',
    noTenantAccess: 'Akaun anda tidak mempunyai akses kepada mana-mana tenant.',
    invalidCredentials: 'Nama pengguna atau kata laluan tidak sah.',
    bridgeFailed: 'Gagal meneruskan log masuk',
  },

  // ── Dashboard ──
  dashboard: {
    title: 'Papan Pemuka',
    welcome: 'Selamat kembali, {{name}}',
    goodMorning: 'Selamat pagi',
    goodAfternoon: 'Selamat petang',
    goodEvening: 'Selamat malam',
    fields: 'medan',
    children: 'anak',
    relations: 'hubungan',
  },

  // ── Per-category workspace pages ──
  workspace: {
    overview:        'Gambaran Keseluruhan',
    subtitle:        '{{count}} entiti',
    quickLinks:      'Pautan Pantas',
    noEntities:      'Tiada entiti dalam ruang kerja ini',
    noEntitiesDesc:  'Konfigurasikan entiti untuk kategori ini dalam panel admin.',
    notFound:        'Ruang kerja tidak ditemui',
    notFoundDesc:    'Ruang kerja ini tidak dikonfigurasi atau anda tidak mempunyai akses.',
  },

  // ── Sidebar category labels ──
  category: {
    // ── Core ──
    home:         'Laman Utama',
    builder:      'Business Builder',
    inbox:        'Peti Masuk',
    business:     'Perniagaan',
    // ── Verticals ──
    transport:    'Pengangkutan Klasik',
    fleet:        'Fleet',
    freight:      'Pilot Kargo',
    warehouse:    'Aliran Gudang',
    haulage:      'Komando Haulage',
    'supply-chain': 'Navigator Rantaian Bekalan',
    service:      'Pengurusan Perkhidmatan',
    // ── Support ──
    finance:      'Kewangan',
    workforce:    'Tenaga Kerja',
    // ── Admin (bottom-pinned) ──
    settings:     'Tetapan',
    platform:     'Platform',
    // ── Legacy (backward compat) ──
    tms:          'TMS',
    fms:          'FMS',
    wms:          'WMS',
    accounting:   'Perakaunan',
    ai:           'AI',
    'ai-command': 'Perintah AI',
    trucking:     'Trak',
    accounts:     'Akaun',
    parties:      'Pihak',
    messaging:    'Mesej',
    agent:        'Ejen',
    config:       'Konfigurasi',
    master:       'Data Induk',
    transaction:  'Operasi',
    event:        'Peristiwa',
    attachment:   'Lampiran',
    communication:'Komunikasi',
    pro2:         'CMS PRO2',
    audit:        'Audit & Pematuhan',
    // ── Network ──
    network:      'Boox Network',
    tenants:      'Pengurusan Penyewa',
    'ai-center':  'Pusat AI',
    'cc-home':      'CC Utama',
    'cc-tenants':   'Operasi Penyewa',
    'cc-brain':     'AI & Otak',
    'cc-builder':   'Pembina',
    'cc-observe':   'Pemerhatian',
    'cc-analytics': 'Analitik',
    'cc-command':   'Command',
    'cc-revenue':   'Revenue Ops',
    'cc-delivery':  'Delivery Ops',
    'cc-ceo':       'CEO',
    'cc-tenant-admin': 'Tenant Admin',
    'cc-systems':   'Platform Systems',
    sas:          'Agensi Perkapalan',
    depot:        'Kawalan Depot',
    soc:          'SOC',
  },

  // ── Sidebar group labels ──
  group: {
    // ── Home groups ──
    Dashboards:       'Papan Pemuka',
    'Quick Access':   'Akses Pantas',
    // ── Inbox groups ──
    Messages:         'Mesej',
    Tasks:            'Tugas',
    // ── Business groups ──
    Sales:            'Jualan',
    Purchase:         'Pembelian',
    // ── Transport / Freight / Warehouse ──
    Operations:       'Operasi',
    Oversea:          'Luar Negara',
    // ── Finance groups ──
    AR:               'Akaun Belum Terima',
    AP:               'Akaun Belum Bayar',
    GL:               'Lejar Am',
    // ── Workforce groups ──
    People:           'Kakitangan',
    Branches:         'Cawangan',
    Access:           'Akses',
    // ── Settings groups ──
    Tenant:           'Penyewa',
    System:           'Sistem',
    // ── Platform groups ──
    'AI Admin':       'Pentadbir AI',
    DevOps:           'DevOps',
    // ── CC (Command Control) groups ──
    Tenants:          'Penyewa',
    Channels:         'Saluran',
    Security:         'Keselamatan',
    Rules:            'Peraturan',
    'AI Config':      'Konfigurasi AI',
    Brain:            'Brain',
    Logs:             'Log',
    Dev:              'Pembangunan',
    Infra:            'Infrastruktur',
    'Platform Intelligence': 'Perisikan Platform',
    overview:         'Overview',
    runtime:          'Runtime',
    tools:            'Tools',
    intake:           'Intake',
    qualification:    'Qualification',
    governance:       'Governance',
    delivery:         'Delivery',
    readiness:        'Readiness',
    executive:        'Executive',
    configuration:    'Configuration',
    build:            'Build',
    'ai-runtime':     'AI Runtime',
    systems:          'Systems',
    monitoring:       'Monitoring',
    // ── Shared groups ──
    Setup:            'Tetapan',
    Reports:          'Laporan',
    'Master Data':    'Data Induk',
    Config:           'Konfigurasi',
    Monitor:          'Monitor',
    // ── Legacy group keys ──
    'Container Trucking': 'Trak Kontena',
    'Local Transport':    'Pengangkutan Tempatan',
    Sea:              'Laut',
    Air:              'Udara',
    'Local Handling': 'Pengendalian Tempatan',
    Customers:        'Pelanggan',
    'Business Parties': 'Pihak Perniagaan',
    Employees:        'Pekerja',
    AI:               'AI',
    Driver:           'Pemandu',
    Workflow:         'Aliran Kerja',
    work:             'Kerja',
    setup:            'Tetapan',
    transactions:     'Transaksi',
    inventory:        'Inventori',
    reports:          'Laporan',
    tax:              'Cukai',
    customers:        'Pelanggan',
    partners:         'Rakan',
    team:             'Pasukan',
    inbox:            'Peti Masuk',
    email:            'E-mel',
    channels:         'Saluran',
    pipeline:         'Saluran Paip',
    tasks:            'Tugas',
    files:            'Fail',
    audit:            'Audit',
    ui:               'Templat UI',
    system:           'Sistem',
    // ── Entity category labels ──
    people:       'Orang',
    jobs:         'Kerja',
    shipping:     'Penghantaran',
    finance:      'Kewangan',
    warehouse:    'Gudang',
    messaging:    'Pemesejan',
    'inventory-events': 'Peristiwa Inventori',
    // ── Module nav group labels (lowercase, used by MODULE_NAV_ITEMS) ──
    dashboard:        'Papan Pemuka',
    orders:           'Pesanan',
    operations:       'Operasi',
    import:           'Import',
    export:           'Eksport',
    master:           'Data Induk',
    Import:           'Import',
    Export:           'Eksport',
    Master:           'Data Induk',
    overall:          'Keseluruhan',
    operating:        'Operasi',
    category:         'Kategori',
    planning:         'Perancangan',
    banking:          'Perbankan',
    admin:            'Pentadbiran',
    'self-service':   'Layan Diri',
    config:           'Konfigurasi',
    tenants:          'Penyewa',
    policies:         'Polisi',
    agents:           'Ejen',
    knowledge:        'Pengetahuan',
    schema:           'Skema',
    templates:        'Templat',
    automation:       'Automasi',
    devtools:         'Alat Pembangun',
    integration:      'Integrasi',
    deployment:       'Penerapan',
    communication:    'Komunikasi',
    breakdown:        'Pecahan',
    realtime:         'Masa Nyata',
    trends:           'Trend',
    // ── Finance module groups (new) ──
    processes:        'Proses',
    management:       'Laporan Pengurusan',
    einvoicing:       'E-Invois',
    compliance:       'Pematuhan',
    // ── Other new groups ──
    review:           'Semakan',
    crm:              'RM',
    messages:         'Mesej',
    schedule:         'Jadual',
    docs:             'Dokumen',
    // ── Network groups ──
    collaboration:    'Kerjasama',
    // ── Module sidebar groups (used by MODULE_NAV_ITEMS) ──
    ai:               'AI',
    'boox-network':   'Rangkaian Boox',
    circles:          'Bulatan',
    contacts:         'Kenalan',
    fulfillment:      'Pemenuhan',
    personal:         'Peribadi',
    procurement:      'Perolehan',
    requests:         'Permintaan',
    resources:        'Sumber',
    safety:           'Keselamatan',
    sales:            'Jualan',
    sourcing:         'Perolehan Sumber',
    tenant:           'Penyewa',
    views:            'Paparan',
    "collections": "Koleksi",
    "control": "Kawalan",
    "core": "teras",
    "devops": "DevOps",
    "execution": "Perlaksanaan",
    "fleet": "Armada",
    "master-data": "Data Induk",
    "ops": "operasi",
    "platform": "Platform",
    "workshop": "Bengkel",
    "workspace": "Ruang kerja",
  },

  // ── Entity list ──
  list: {
    totalRecords: '{{count}} rekod',
    newBtn: 'Baru',
    cancel: 'Batal',
    createTitle: '{{label}} Baharu',
    createSuccess: '{{label}} dicipta',
    search: 'Cari {{label}}…',
    allStatus: 'Semua status',
    loading: 'Memuatkan…',
    noData: 'Tiada data',
    page: 'Halaman {{current}} / {{total}}',
    loadFailed: 'Gagal memuatkan data',
    notFound: 'Entiti tidak dijumpai',
  },

  // ── Entity detail ──
  detail: {
    loadFailed: 'Gagal memuatkan butiran',
    notFound: 'Rekod tidak dijumpai',
    confirmDeleteTitle: 'Sahkan Padam',
    confirmDeleteDesc: 'Adakah anda pasti mahu memadamkan rekod {{label}} ini? Tindakan ini tidak boleh dibatalkan.',
    delete: 'Padam',
    deleteSuccess: 'Dipadamkan',
    deleteFailed: 'Padam gagal',
    actionSuccess: '{{action}} dilaksanakan',
    actionFailed: '{{action}} gagal',
    cancelEdit: 'Batal Edit',
    edit: 'Edit',
    editTitle: 'Edit {{label}}',
    tabHuman: 'Paparan Penuh',
    tabBasic: 'Asas',
    tabJson: 'JSON',
  },

  // ── Entity edit form ──
  form: {
    required: '{{field}} diperlukan',
    minValue: 'Nilai minimum ialah {{min}}',
    maxValue: 'Nilai maksimum ialah {{max}}',
    invalidFormat: 'Format tidak sah',
    selectPlaceholder: 'Pilih…',
    boolTrue: 'Ya',
    boolFalse: 'Tidak',
    saveSuccess: 'Disimpan',
    createSuccess: 'Dicipta',
    saveFailed: 'Simpan gagal',
    save: 'Simpan',
    create: 'Cipta',
    cancel: 'Batal',
  },

  // ── Human view ──
  humanView: {
    summary: 'Ringkasan',
    relatedData: 'Data Berkaitan',
    childEntities: 'Entiti Anak',
    moreRows: '{{count}} lagi…',
    boolTrue: 'Ya',
    boolFalse: 'Tidak',
    empty: '—',
  },

  // ── Basic view ──
  basicView: {
    basicInfo: 'Maklumat Asas',
    extendedData: 'Data Lanjutan',
    boolTrue: 'Ya',
    boolFalse: 'Tidak',
    empty: '—',
  },

  // ── View template fallback sections ──
  viewTemplate: {
    basicInfo: 'Maklumat Asas',
    financialInfo: 'Maklumat Kewangan',
    timeline: 'Garis Masa',
    otherInfo: 'Maklumat Lain',
  },

  // ── Export ──
  export: {
    csv: 'Eksport CSV',
    success: 'Eksport selesai',
    failed: 'Eksport gagal, cuba lagi',
  },

  // ── Notifications ──
  notification: {
    title: 'Pemberitahuan',
    markAllRead: 'Tandakan semua dibaca',
    loading: 'Memuatkan…',
    empty: 'Tiada pemberitahuan',
    justNow: 'baru sahaja',
    minutesAgo: '{{count}} min lalu',
    hoursAgo: '{{count}} jam lalu',
    daysAgo: '{{count}} hari lalu',
  },

  // ── Confirm dialog ──
  confirm: {
    ok: 'Sahkan',
    cancel: 'Batal',
    processing: 'Memproses…',
  },

  // ── Error boundary ──
  error: {
    title: 'Sesuatu telah berlaku',
    fallback: 'Ralat yang tidak dijangka berlaku.',
    tryAgain: 'Cuba Lagi',
    goHome: 'Ke Laman Utama',
  },

  // ── Search / Command Palette ──
  search: {
    placeholder: 'Cari entiti… ⌘K',
    noResults: 'Tiada entiti yang sepadan',
    navigate: 'Navigasi',
    open: 'Buka',
    results: '{{count}} keputusan',
  },

  // ── User dropdown ──
  user: {
    profile: 'Profil',
    settings: 'Tetapan',
    shortcuts: 'Pintasan Papan Kekunci',
    signOut: 'Log Keluar',
    switchTenant: 'Tukar Syarikat',
  },

  // ── Navigation actions ──
  nav: {
    chat: 'Sembang',
    home: 'Laman Utama',
    teamspace: 'Teamspace',
    ceoDesk: 'CEO Desk',
    booxCommand: 'Boox Command',
    aiChat: 'AI Chat',
    brainMonitor: 'Brain Monitor',
    runtimeDebug: 'Runtime Debug',
    alertCenter: 'Alert Center',
    rolloutDashboard: 'Rollout Dashboard',
    revenueInbox: 'Revenue Inbox',
    messagingIntake: 'Messaging Intake',
    surfaceAccess: 'Surface Access',
    partnerApplications: 'Partner Applications',
    onboardingControl: 'Onboarding Control',
    onboardingRecords: 'Onboarding Records',
    assignmentPolicies: 'Assignment Policies',
    capabilityMatrix: 'Capability Matrix',
    stateConsole: 'State Console',
    eInvoicingOps: 'E-Invoicing Ops',
    strategy: 'Strategy',
    fundraising: 'Fundraising',
    dataRoom: 'Data Room',
    aiCoCeo: 'AI Co-CEO',
    tenants: 'Tenants',
    globalUsers: 'Global Users',
    userRoles: 'User Roles',
    authControl: 'Auth Control',
    capabilities: 'Capabilities',
    policies: 'Policies',
    exceptionPolicies: 'Exception Policies',
    workflowPolicies: 'Workflow Policies',
    whatsAppAdmin: 'WhatsApp',
    builder: 'Builder',
    businessPackages: 'Business Packages',
    platformSystems: 'Platform Systems',
    configCompiler: 'Config Compiler',
    configVersions: 'Config Versions',
    domainConfigs: 'Domain Configs',
    menuConfig: 'Menu Config',
    bridges: 'Bridges',
    dataTransfer: 'Data Transfer',
    deployControl: 'Deploy Control',
    printCenter: 'Print Center',
    platformAnalytics: 'Platform Analytics',
    logExplorer: 'Log Explorer',
    schemaEditor: 'Schema Editor',
    dslDesigner: 'DSL Designer',
    viewTemplates: 'View Templates',
    documentTemplates: 'Document Templates',
    workflows: 'Workflows',
    workflowTemplates: 'Workflow Templates',
    scripts: 'Scripts',
    aiCommandCenter: 'AI Command Center',
    aiTeam: 'AI Team',
    agentLab: 'Agent Lab',
    skills: 'Skills',
    skillConfig: 'Skill Config',
    prompts: 'Prompts',
    trainingData: 'Training Data',
    memory: 'Memory',
    tools: 'Tools',
    aiControlPlane: 'AI Control Plane',
    brainLogs: 'Brain Logs',
    brainSoul: 'Brain Soul',
    brainSchedule: 'Brain Schedule',
    recurringTasks: 'Recurring Tasks',
    taskCenter: 'Task Center',
    executiveNudges: 'Executive Nudges',
    apiCenter: 'API Center',
    accessLog: 'Access Log',
    userPerformance: 'User Performance',
    jobTimeline: 'Job Timeline',
    pro2: {
      importShipment: 'Penghantaran Import',
      importShipments: 'Penghantaran Import',
      importContainer: 'Kontena Import',
      importContainers: 'Kontena Import',
      importCargo: 'Kargo Import',
      exportShipment: 'Penghantaran Eksport',
      exportShipments: 'Penghantaran Eksport',
      exportBooking: 'Tempahan Eksport',
      exportBookings: 'Tempahan Eksport',
      exportContainer: 'Kontena Eksport',
      exportContainers: 'Kontena Eksport',
      exportCargo: 'Kargo Eksport',
      invCostNote: 'Invois / Nota Kos',
      invCostNotes: 'Invois / Nota Kos',
      chargeLine: 'Baris Caj',
      chargeLines: 'Baris Caj',
      company: 'Syarikat',
      companies: 'Syarikat',
    },
    Dashboards: 'Papan Pemuka',
    'Team Monitor': 'Monitor Pasukan',
    Shortcuts: 'Pintasan',
    Recent: 'Terkini',
    'All Messages': 'Semua Mesej',
    Emails: 'E-mel',
    WhatsApp: 'WhatsApp',
    'My Tasks': 'Tugas Saya',
    Approvals: 'Kelulusan',
    Alerts: 'Amaran',
    Orders: 'Pesanan',
    Quotations: 'Sebut Harga',
    Pipelines: 'Saluran Jualan',
    Goals: 'Matlamat',
    'Purchase Orders': 'Pesanan Belian',
    'Vendor Sourcing': 'Sumber Vendor',
    Customers: 'Pelanggan',
    Vendors: 'Vendor',
    Contacts: 'Kenalan',
    'Sales Reports': 'Laporan Jualan',
    Jobs: 'Kerja',
    Planner: 'Perancang',
    'FCL Planner': 'Perancang FCL',
    'LCL Planner': 'Perancang LCL',
    Trips: 'Perjalanan',
    'Live Tracking': 'Penjejakan Langsung',
    Dispatch: 'Penghantaran',
    'Master Data': 'Data Induk',
    Drivers: 'Pemandu',
    Vehicles: 'Kenderaan',
    Routes: 'Laluan',
    Billing: 'Pengebilan',
    'Sea Imports': 'Import Laut',
    'Sea Exports': 'Eksport Laut',
    'Air Imports': 'Import Udara',
    'Air Exports': 'Eksport Udara',
    'Port System': 'Sistem Pelabuhan',
    CFS: 'CFS',
    Customs: 'Kastam',
    Documents: 'Dokumen',
    Rates: 'Kadar',
    Carriers: 'Pengangkut',
    Vessels: 'Kapal',
    Accounting: 'Perakaunan',
    'Volume Reports': 'Laporan Volum',
    Inbound: 'Masuk',
    Inventory: 'Inventori',
    Outbound: 'Keluar',
    'Work Orders': 'Perintah Kerja',
    Warehouses: 'Gudang',
    Locations: 'Lokasi',
    'Stock Reports': 'Laporan Stok',
    Movements: 'Pergerakan',
    Invoices: 'Invois',
    Receipts: 'Resit',
    'AR Aging': 'Penuaan AR',
    'Vendor Bills': 'Bil Pembekal',
    Payments: 'Pembayaran',
    'Journal Entries': 'Catatan Jurnal',
    'Chart of Accounts': 'Carta Akaun',
    'Bank Account': 'Akaun Bank',
    'Bank Accounts': 'Akaun Bank',
    Statement: 'Penyata',
    'Bank Statements': 'Penyata Bank',
    Reconciliation: 'Rekonsiliasi',
    'Charge Code': 'Kod Caj',
    'Charge Codes': 'Kod Caj',
    'Tax Jurisdiction': 'Bidang Kuasa Cukai',
    'Tax Jurisdictions': 'Bidang Kuasa Cukai',
    'Tax Codes': 'Kod Cukai',
    'Tax Rule': 'Peraturan Cukai',
    'Tax Rules': 'Peraturan Cukai',
    'P&L Reports': 'Laporan Untung Rugi',
    'Balance Sheets': 'Kunci Kira-Kira',
    Shipment: 'Penghantaran',
    Shipments: 'Penghantaran',
    Overview: 'Gambaran Keseluruhan',
    Accounts: 'Akaun',
    Bookings: 'Tempahan',
    Documentation: 'Dokumentasi',
    Release: 'Pelepasan',
    'Local Services': 'Perkhidmatan Tempatan',
    Schedules: 'Jadual',
    'Principal Tasks': 'Tugas Prinsipal',
    'AI Intake': 'Intake AI',
    Yard: 'Depoh',
    Moves: 'Pergerakan',
    Maintenance: 'Penyelenggaraan',
    Exceptions: 'Pengecualian',
    Releases: 'Pelepasan',
    Repairs: 'Pembaikan',
    Container: 'Kontena',
    Containers: 'Kontena',
    Cargo: 'Kargo',
    Costing: 'Kos',
    Import: 'Import',
    Export: 'Eksport',
    Transhipment: 'Transhipment',
    'Cross Trade': 'Perdagangan Silang',
    Local: 'Tempatan',
    Consol: 'Konsol',
    Air: 'Udara',
    'Shipment Report': 'Laporan Penghantaran',
    'Billing Report': 'Laporan Bil',
    'Imp Shipment': 'Penghantaran Import',
    'Imp Container': 'Kontena Import',
    'Imp Cargo': 'Kargo Import',
    'Exp Shipment': 'Penghantaran Eksport',
    'Exp Booking': 'Tempahan Eksport',
    'Exp Container': 'Kontena Eksport',
    'Exp Cargo': 'Kargo Eksport',
    'Inv / Cost Note': 'Invois / Nota Kos',
    'Charge Lines': 'Baris Caj',
    'Quotation / Rates': 'Sebutharga / Kadar',
    'Job Order': 'Pesanan Kerja',
    'Job Orders': 'Pesanan Kerja',
    'By Customer': 'Mengikut Pelanggan',
    'By Date': 'Mengikut Tarikh',
    'Driver Log': 'Log Pemandu',
    'Driver Pay': 'Bayaran Pemandu',
    Employees: 'Pekerja',
    Departments: 'Jabatan',
    Branches: 'Cawangan',
    Roles: 'Peranan',
    Permissions: 'Kebenaran',
    'General Settings': 'Tetapan Umum',
    'My Profile': 'Profil Saya',
    'Company Settings': 'Tetapan Syarikat',
    Team: 'Pasukan',
    // ── CC (Command Control) nav items ──
    Home: 'Laman Utama',
    Tenants: 'Penyewa',
    'Users & Roles': 'Pengguna & Peranan',
    Capabilities: 'Keupayaan',
    'Capability Matrix': 'Matriks Keupayaan',
    'WhatsApp Config': 'Konfigurasi WhatsApp',
    'Menu Config': 'Konfigurasi Menu',
    'Domain Configs': 'Konfigurasi Domain',
    'Auth Control': 'Kawalan Auth',
    'Command Center': 'Pusat Arahan',
    'AI Team': 'Pasukan AI',
    Skills: 'Kemahiran',
    'Skill Config': 'Konfigurasi Kemahiran',
    'Rules & Policies': 'Peraturan & Polisi',
    'Exception Policies': 'Polisi Pengecualian',
    'Workflow Policies': 'Polisi Aliran Kerja',
    Prompts: 'Gesaan',
    'Training Data': 'Data Latihan',
    'Memory Manager': 'Pengurus Memori',
    'Brain Monitor': 'Monitor Brain',
    'Brain Logs': 'Log Brain',
    'Brain Soul': 'Soul Brain',
    'Brain Schedule': 'Jadual Brain',
    'Thread Logs': 'Log Thread',
    'Agent Lab': 'Makmal Ejen',
    Messaging: 'Pemesejan',
    'Email Logs': 'Log E-mel',
    'Email Accounts': 'Akaun E-mel',
    'WhatsApp Groups': 'Kumpulan WhatsApp',
    'Platform Admin': 'Admin Platform',
    'Rollout Dashboard': 'Papan Pemuka Pelancaran',
    'Deploy Control': 'Kawalan Deployment',
    'AI Control Plane': 'Satah Kawalan AI',
    'Bridge Registry': 'Registry Bridge',
    'Tool Registry': 'Registry Alat',
    'Doc Templates': 'Templat Dokumen',
    'Schema Editor': 'Editor Skema',
    Workflows: 'Aliran Kerja',
    'WF Templates': 'Templat Aliran Kerja',
    Scripts: 'Skrip',
    'Config Versions': 'Versi Konfigurasi',
    'Config Compiler': 'Pengkompil Konfigurasi',
    'View Templates': 'Templat Paparan',
    'DSL Designer': 'Pereka DSL',
    'Print Center': 'Pusat Cetak',
    // ── CC additional labels ──
    Rollout: 'Pelancaran',
    'AI Chat': 'Sembang AI',
    'Global Users': 'Pengguna Global',
    'User Roles': 'Peranan Pengguna',
    Policies: 'Polisi',
    Schedule: 'Jadual',
    Tools: 'Alat',
    Memory: 'Memori',
    'Email Intake': 'Pengambilan E-mel',
    'Log Explorer': 'Penjelajah Log',
    'Job Timeline': 'Garis Masa Kerja',
    Bridges: 'Jambatan',
    'Email Inbox': 'Peti Masuk E-mel',
    'Platform KPI': 'KPI Platform',
    'By Company': 'Mengikut Syarikat',
    'By Region': 'Mengikut Wilayah',
    'By Port Pair': 'Mengikut Pasangan Pelabuhan',
    'Haulage Live': 'Pengangkutan Langsung',
    'Freight Volume': 'Jumlah Kargo',
    'WMS Throughput': 'Throughput WMS',
    'Growth & Churn': 'Pertumbuhan & Churan',
    'AI Efficiency': 'Kecekapan AI',
    // ── Driver PWA nav ──
    'My Trips': 'Perjalanan Saya',
    'My Pay': 'Gaji Saya',
    Driver: 'Pemandu',
    // ── Module nav items (used by MODULE_NAV_ITEMS in sidebar) ──
    // Home
    Dashboard: 'Papan Pemuka',
    Today: 'Hari Ini',
    Inbox: 'Peti Masuk',
    Email: 'E-mel',
    'AI Review': 'Semakan AI',
    Directory: 'Direktori',
    Connections: 'Sambungan',
    Messages: 'Mesej',
    Channels: 'Saluran',
    // Business (OMS)
    Pipeline: 'Saluran Jualan',
    'Business Party': 'Pihak Perniagaan',
    'Business Parties': 'Pihak Perniagaan',
    Customer: 'Pelanggan',
    Circles: 'Bulatan',
    Group: 'Kumpulan',
    Groups: 'Kumpulan',
    RFQ: 'Permintaan Sebut Harga',
    // Transport (TMS)
    'Event Log': 'Log Peristiwa',
    // Haulage (HMS)
    'Control Room': 'Bilik Kawalan',
    'AI CS Manager': 'Pengurus CS AI',
    'Staff Performance': 'Prestasi Kakitangan',
    'TR128 Audit': 'Audit TR128',
    'CO2e Report': 'Laporan CO2e',
    // Freight (FMS)
    'Sea Schedule': 'Jadual Pelayaran',
    // PRO2
    'Import Shipment': 'Penghantaran Import',
    'Import Shipments': 'Penghantaran Import',
    'Import Container': 'Kontena Import',
    'Import Containers': 'Kontena Import',
    'Import Cargo': 'Kargo Import',
    'Export Shipment': 'Penghantaran Eksport',
    'Export Shipments': 'Penghantaran Eksport',
    'Export Booking': 'Tempahan Eksport',
    'Export Bookings': 'Tempahan Eksport',
    'Export Container': 'Kontena Eksport',
    'Export Containers': 'Kontena Eksport',
    'Export Cargo': 'Kargo Eksport',
    'Invoice / Cost Note': 'Invois / Nota Kos',
    'Invoice / Cost Notes': 'Invois / Nota Kos',
    'Charge Line': 'Baris Caj',
    Company: 'Syarikat',
    Companies: 'Syarikat',
    // Supply Chain (SCM)
    'Sales Order': 'Pesanan Jualan',
    'Sales Orders': 'Pesanan Jualan',
    Quotation: 'Sebut Harga',
    Salesperson: 'Jurujual',
    Salespersons: 'Jurujual',
    'Purchase Order': 'Pesanan Belian',
    Supplier: 'Pembekal',
    Suppliers: 'Pembekal',
    Product: 'Produk',
    Products: 'Produk',
    'Stock Movement': 'Pergerakan Stok',
    'Stock Movements': 'Pergerakan Stok',
    Fulfillment: 'Pemenuhan',
    'Shipment Tracking': 'Penjejakan Penghantaran',
    Reports: 'Laporan',
    // Service (SVC)
    'Service Order': 'Pesanan Perkhidmatan',
    'Service Orders': 'Pesanan Perkhidmatan',
    Equipment: 'Peralatan',
    Crew: 'Kru',
    Crews: 'Kru',
    Sites: 'Tapak',
    // Warehouse (WMS)
    Tasks: 'Tugas',
    'QC / Inspection': 'QC / Pemeriksaan',
    'Warehouse Tasks': 'Tugas Gudang',
    'Arrival Dock': 'Dok Ketibaan',
    PO: 'Pesanan Belian',
    'Material Request': 'Permintaan Bahan',
    'Material Requests': 'Permintaan Bahan',
    'Material Return': 'Pemulangan Bahan',
    'Inventory Transfer': 'Pemindahan Inventori',
    'Inventory Return': 'Pulangan Inventori',
    'Shared Documents': 'Dokumen Dikongsi',
    '3PL Management': 'Pengurusan 3PL',
    Supervisor: 'Penyelia',
    'Executive KPI': 'KPI Eksekutif',
    'CS Workbench': 'Meja Kerja CS',
    // Finance
    'Accounts Receivable': 'Akaun Belum Terima',
    'Accounts Payable': 'Akaun Belum Bayar',
    'Journal Entry': 'Catatan Jurnal',
    'AR Collection': 'Kutipan AR',
    'Payment Run': 'Proses Pembayaran',
    'Payment Runs': 'Proses Pembayaran',
    'AP Aging': 'Penuaan AP',
    'Statement of A/c': 'Penyata Akaun',
    'Statements of A/c': 'Penyata Akaun',
    'Tax Report': 'Laporan Cukai',
    'Tax Reports': 'Laporan Cukai',
    'General Ledger': 'Lejar Am',
    'Trial Balance': 'Imbangan Duga',
    'Profit & Loss': 'Untung & Rugi',
    'Balance Sheet': 'Kunci Kira-kira',
    'Cash Flow': 'Aliran Tunai',
    'Management A/c': 'Akaun Pengurusan',
    'Management Accounts': 'Akaun Pengurusan',
    'Audit Report': 'Laporan Audit',
    'Audit Reports': 'Laporan Audit',
    'Process Audit': 'Audit Proses',
    'Process Audits': 'Audit Proses',
    Consolidation: 'Gabungan',
    'E-Invoicing': 'E-Invois',
    'Audit Overview': 'Gambaran Audit',
    Settlement: 'Penyelesaian',
    Disputes: 'Pertikaian',
    'Bill Audit': 'Audit Bil',
    'Bill Audits': 'Audit Bil',
    'Accounting Periods': 'Tempoh Perakaunan',
    'Tax Config': 'Konfigurasi Cukai',
    'Exchange Rates': 'Kadar Pertukaran',
    'Legal Entities': 'Entiti Undang-undang',
    // Workforce (HRM)
    Attendance: 'Kehadiran',
    Payroll: 'Gaji',
    'Leave Types': 'Jenis Cuti',
    'Leave Mgmt': 'Pengurusan Cuti',
    Holidays: 'Cuti Umum',
    'Holiday Calendar': 'Kalendar Cuti',
    Loans: 'Pinjaman',
    'Claims Approval': 'Kelulusan Tuntutan',
    Safety: 'Keselamatan',
    Incidents: 'Insiden',
    Checklists: 'Senarai Semak',
    Signoffs: 'Pengesahan',
    Training: 'Latihan',
    Briefings: 'Taklimat',
    'Safety Briefings': 'Taklimat Keselamatan',
    'Work Summaries': 'Ringkasan Kerja',
    'My Leave': 'Cuti Saya',
    'My Attendance': 'Kehadiran Saya',
    'My Payslip': 'Slip Gaji Saya',
    'My Loans': 'Pinjaman Saya',
    'My Claims': 'Tuntutan Saya',
    // Settings
    General: 'Am',
    Profile: 'Profil',
    'PWA Settings': 'Tetapan PWA',
    'PWA Install Policies': 'Dasar Pemasangan PWA',
    'Surface Access': 'Akses Permukaan',
    'Team Members': 'Ahli Pasukan',
    Templates: 'Templat',
    'Document Templates': 'Templat Dokumen',
    'Knowledge Base': 'Pangkalan Pengetahuan',
    Builder: 'Pembina',
    'Builder Studio': 'Studio Pembina',
    'Business Packages': 'Pakej Perniagaan',
    'Package Exchange': 'Pertukaran Pakej',
    "AI Co-CEO": "Ketua Pegawai Eksekutif AI",
    "AI Command Center": "Pusat Perintah AI",
    "API Center": "Pusat API",
    "AR/AP Netting": "Jaringan AR/AP",
    "Access Log": "Log Akses",
    "Activities": "Aktiviti",
    "Admin Setup": "Persediaan Pentadbir",
    "Agency Accounts": "Akaun Agensi",
    "Alert Center": "Pusat Makluman",
    "Alliance": "Perikatan",
    "Alliances": "Pakatan",
    "Amber": "Amber",
    "Appraisal": "Penilaian",
    "Assignment Policies": "Dasar Tugasan",
    "Bahasa Indonesia": "Bahasa Indonesia",
    "Bahasa Melayu": "Bahasa Melayu",
    "Blue": "Biru",
    "Boox Command": "Perintah Box",
    "CEO Desk": "Meja Ketua Pegawai Eksekutif",
    "RM Intelligence Rules": "Peraturan Perisikan RM",
    "Commercial": "Komersil",
    "Contracts": "Kontrak",
    "Daily Schedule": "Jadual Harian",
    "Data Room": "Bilik Data",
    "Data Transfer": "Pemindahan Data",
    "Design Copilot": "Copilot Reka Bentuk",
    "E-Invoicing Ops": "Ops E-Invois",
    "English": "Inggeris",
    "Executive Nudges": "Tunduk Eksekutif",
    "Feeder Bookings": "Tempahan Feeder",
    "Finance": "Kewangan",
    "Financial Setup": "Persediaan Kewangan",
    "Fixed Assets": "Aset Tetap",
    "Fundraising": "Pengumpulan dana",
    "Gate": "Gerbang",
    "Green": "hijau",
    "Incentives": "Insentif",
    "Indigo": "nila",
    "Inspection": "Pemeriksaan",
    "Issue Orders": "Keluarkan Perintah",
    "Kudos Wall": "Kudos Wall",
    "LLM Intake Test": "Ujian Pengambilan LLM",
    "MCP Management": "Pengurusan MCP",
    "Messaging Intake": "Pengambilan Pesanan",
    "Onboarding Assignment Policies": "Dasar Penyerahan Tugasan",
    "Onboarding Control": "Kawalan Onboarding",
    "Onboarding Records": "Rekod Onboarding",
    "Part": "Bahagian",
    "Parts": "bahagian",
    "Partner Applications": "Aplikasi Rakan Kongsi",
    "Payroll & Compliance": "Gaji & Pematuhan",
    "Performance Appraisals": "Penilaian Prestasi",
    "Pink": "Merah jambu",
    "Platform Analytics": "Analitis Platform",
    "Platform Systems": "Sistem Platform",
    "Port Call Docs": "Dokumen Panggilan Pelabuhan",
    "Pricing": "penetapan harga",
    "Profitability": "Keberuntungan",
    "Purple": "Ungu",
    "REST API": "REST API",
    "Re-use Leads": "Guna Semula Petunjuk",
    "Recovery": "Pemulihan",
    "Recurring Tasks": "Tugasan Berulang",
    "Revenue": "Hasil",
    "Revenue Inbox": "Peti Masuk Hasil",
    "Rose": "Mawar",
    "Runtime Debug": "Nyahpepijat Masa Jalan",
    "Safety Hub": "Hab Keselamatan",
    "Schedule Board": "Papan Jadual",
    "Settings": "tetapan",
    "Setup": "Persediaan",
    "Shift Swap Requests": "Permintaan Shift Swap",
    "Shift Swaps": "Shift Swap",
    "Site Ops": "Ops Tapak",
    "State Console": "Konsol Negeri",
    "Stock Ledger": "Lejar Stok",
    "Strategy": "Strategi",
    "Tally Control": "Kawalan Tally",
    "Tariff": "Tarif",
    "Task Center": "Pusat Tugas",
    "Tax Configuration": "Konfigurasi Cukai",
    "Teal": "Teal",
    "Teamspace": "Ruang pasukan",
    "Tiếng Việt": "Tiếng Việt",
    "Tyre": "tayar",
    "Tyres": "tayar",
    "User Performance": "Prestasi Pengguna",
    "Vehicle": "kenderaan",
    "WMS": "WMS",
    "WMS Setup": "Persediaan WMS",
    "Workflow Templates": "Templat Aliran Kerja",
    "ไทย": "ไทย",
    "中文": "中文",
    "日本語": "日本語",
    "繁體中文": "繁體中文",
  },

  networkPage: {
    common: {
      close: 'Tutup',
      cancel: 'Batal',
      refresh: 'Muat Semula',
      unknown: 'Tidak Diketahui',
    },
    circleTypes: {
      ownership: 'Kumpulan Pemilikan',
      agent_network: 'Rangkaian Ejen',
      vendor_panel: 'Panel Vendor',
      peer_circle: 'Bulatan Rakan',
      consortium: 'Konsortium',
    },
    groupEntityTypes: {
      hq: 'Ibu Pejabat',
      subsidiary: 'Anak Syarikat',
      branch: 'Cawangan',
      associate: 'Syarikat Bersekutu',
      default: 'Anak Syarikat',
    },
    circles: {
      memberCount: '{{count}} ahli',
      owner: 'Pemilik',
      admin: 'Pentadbir',
      detailLoadFailed: 'Gagal memuatkan butiran bulatan',
      nameRequired: 'Sila masukkan nama bulatan',
      createdSuccess: 'Bulatan dicipta',
      createFailed: 'Gagal mencipta bulatan',
      listLoadFailed: 'Gagal memuatkan senarai bulatan',
      createTitle: 'Cipta Bulatan Baharu',
      nameLabel: 'Nama Bulatan *',
      namePlaceholder: 'Contoh: Rangkaian Ejen CMS',
      typeLabel: 'Jenis Bulatan *',
      descLabel: 'Penerangan (pilihan)',
      descPlaceholder: 'Terangkan secara ringkas tujuan bulatan ini',
      createSubmitting: 'Mencipta...',
      createAction: 'Cipta Bulatan',
      title: 'Rangkaian Perdagangan & Bulatan',
      subtitle: 'Asingkan kumpulan pemilikan eksklusif daripada rangkaian perdagangan dan bulatan kerjasama yang bertindih',
      ownershipNoteBadge: 'Kumpulan Pemilikan',
      ownershipNoteDesc: 'Setiap syarikat hanya tergolong dalam satu kumpulan pemilikan. Hubungan itu diuruskan secara berasingan.',
      openGroupPage: 'Buka Halaman Kumpulan',
      emptyTitle: 'Anda belum menyertai mana-mana bulatan',
      emptyDesc: 'Cipta bulatan pertama anda untuk mula bekerjasama dengan rakan niaga',
      typeCount: '{{count}} item',
      membersTitle: 'Ahli ({{count}})',
      tradeNetworksTitle: 'Rangkaian Perdagangan',
      tradeNetworksDesc: 'Rangkaian agensi bertindih, pakatan ko-muatan, dan rangkaian perniagaan rasmi.',
      tradeNetworksEmptyDesc: 'Belum ada rangkaian perdagangan. Cipta satu untuk hubungan perniagaan bertindih.',
      circlesSectionTitle: 'Bulatan',
      circlesSectionDesc: 'Bulatan operasi kecil seperti trak, pengangkut, ko-muatan, atau kumpulan kerjasama berasaskan rakan.',
      circlesSectionEmptyDesc: 'Belum ada bulatan. Cipta bulatan kerjasama tempatan untuk vendor atau rakan niaga.',
    },
    groups: {
      current: 'Semasa',
      switch: 'Tukar',
      loadFailed: 'Gagal memuatkan maklumat kumpulan',
      switchSuccess: 'Ruang kerja ditukar. Memuat semula...',
      switchFailed: 'Penukaran gagal. Sila cuba lagi.',
      title: 'Kumpulan Pemilikan',
      subtitle: 'Setiap syarikat hanya tergolong dalam satu kumpulan. Lihat syarikat ahli, ibu pejabat, asas pejabat, dan tukar ruang kerja dalam kumpulan.',
      emptyTitle: 'Akaun anda belum menyertai mana-mana kumpulan',
      emptyDesc: 'Hubungi pentadbir platform untuk menambah syarikat anda ke dalam kumpulan',
      summary: 'Kumpulan Pemilikan · {{count}} syarikat ahli',
      roleFallback: 'Ahli',
      exclusiveBadge: 'Eksklusif',
      hqLabel: 'Ibu Pejabat',
      hqCompany: 'Syarikat Ibu Pejabat',
      memberCompanies: 'Syarikat Ahli',
      coverage: 'Liputan Negara',
      companyMembersTitle: 'Syarikat Kumpulan',
      adminMembersTitle: 'Pentadbir & Ahli Kumpulan ({{count}})',
      officeBaseValue: '{{country}} · {{city}}',
      timezoneValue: 'TZ {{timezone}}',
      currencyValue: 'Asas {{currency}}',
      noOfficeBase: 'Asas pejabat belum ditetapkan',
    },
    rfq: {
      pageTitle: 'Ruang Kerja RFQ',
      pageSubtitle: 'Cipta RFQ atau semak permintaan sebut harga yang diterima',
      creator: 'Dicipta oleh saya',
      detailLoadFailed: 'Gagal memuatkan butiran RFQ',
      publishSuccess: 'RFQ diterbitkan. Menunggu sebut harga.',
      publishFailed: 'Gagal menerbitkan RFQ',
      quoteAmountRequired: 'Sila masukkan jumlah sebut harga',
      quoteSubmitSuccess: 'Sebut harga dihantar',
      quoteSubmitFailed: 'Gagal menghantar sebut harga',
      titleRequired: 'Sila masukkan tajuk RFQ',
      draftCreated: 'Draf RFQ dicipta',
      createFailed: 'Gagal mencipta RFQ',
      listLoadFailed: 'Gagal memuatkan senarai RFQ',
      typeSpot: 'RFQ Spot',
      typeContract: 'RFQ Kontrak',
      typeRecurring: 'RFQ Berulang',
      modeSealed: 'Bidaan Tertutup',
      modeOpen: 'Bidaan Terbuka',
      modeMixed: 'Mod Campuran',
      tabInfo: 'Maklumat Asas',
      tabQuotes: 'Sebut Harga',
      tabSubmit: 'Hantar Sebut Harga',
      fieldType: 'Jenis RFQ',
      fieldCurrency: 'Mata Wang',
      fieldDeadline: 'Tarikh Akhir',
      fieldCreatedAt: 'Dicipta Pada',
      cargoSummary: 'Ringkasan Kargo',
      publishSubmitting: 'Menerbitkan...',
      publishAction: 'Terbitkan RFQ dan mula mengumpul sebut harga',
      inviteesTitle: 'Jemputan ({{count}})',
      emptyQuotes: 'Belum ada sebut harga',
      awarded: 'Dianugerahkan',
      submittedAt: 'Dihantar pada {{date}}',
      quoteAmountLabel: 'Jumlah Sebut Harga ({{currency}}) *',
      quoteNotesLabel: 'Nota (pilihan)',
      quoteNotesPlaceholder: 'Syarat, andaian, atau catatan',
      submitSubmitting: 'Menghantar...',
      submitAction: 'Hantar Sebut Harga',
      createTitle: 'Mulakan RFQ Baharu',
      formTitleLabel: 'Tajuk RFQ *',
      formTitlePlaceholder: 'Contoh: Trak Kontena FCL S2 2026 - TANJONG PAGAR',
      formTypeLabel: 'Jenis RFQ',
      formModeLabel: 'Mod Sebut Harga',
      formCircleLabel: 'Bulatan Berkaitan (pilihan)',
      formCirclePlaceholder: 'Tiada sekatan, terbuka kepada seluruh rangkaian',
      formCircleAny: 'Tiada sekatan, terbuka kepada seluruh rangkaian',
      formDeadlineLabel: 'Tarikh Akhir Sebut Harga',
      formDescriptionLabel: 'Penerangan Latar Belakang (pilihan)',
      formDescriptionPlaceholder: 'Butiran kargo, keperluan perkhidmatan, atau nota lain',
      createSubmitting: 'Mencipta...',
      createAction: 'Cipta Draf',
      tabMine: 'Dicipta oleh Saya',
      tabReceived: 'Diterima',
      emptyMineTitle: 'Belum ada RFQ dicipta',
      emptyReceivedTitle: 'Tiada permintaan sebut harga diterima',
      emptyMineDesc: 'Mulakan RFQ untuk mengumpul sebut harga daripada pembekal',
      startAction: 'Mulakan RFQ',
      statuses: {
        draft: 'Draf',
        published: 'Diterbitkan',
        quoting: 'Menyebut Harga',
        evaluating: 'Menilai',
        awarded: 'Dianugerahkan',
        completed: 'Selesai',
        cancelled: 'Dibatalkan',
        expired: 'Tamat Tempoh',
      },
    },
  },

  crmAccount360: {
    health: {
      attention: 'Attention',
      active: 'Active',
      steady: 'Steady',
    },
    title: 'RM Account 360',
    description: 'Commercial snapshot across contacts, deals, follow-ups, quotations, and jobs.',
    askBoox: 'Ask Boox',
    loading: 'Loading RM account snapshot...',
    loadFailed: 'Failed to load RM account 360',
    promptReviewCustomer: 'Review customer {{name}}. Summarize pipeline, overdue follow-ups, latest quotations, and next best commercial action.',
    unnamedContact: 'Unnamed contact',
    primary: 'Primary',
    decisionMaker: 'Decision maker',
    roleNotSet: 'Role not set',
    noContactChannel: 'No contact channel',
    preferred: 'Preferred: {{channel}}',
    keyContacts: 'Key Contacts',
    noContacts: 'No RM contacts yet.',
    pipelineFollowUp: 'Pipeline & Follow-up',
    noOpenActivity: 'No open RM activity for this account yet.',
    expectedClose: 'Expected close {{date}}',
    due: 'Due {{date}}',
    quotesJobs: 'Quotes & Jobs',
    noQuotesJobs: 'No quotations or jobs linked yet.',
    draft: 'draft',
    validUntil: 'Valid until {{date}}',
    jobTypeFallback: 'Job',
    eta: 'ETA {{date}}',
    cards: {
      openDeals: 'Open Deals',
      weighted: 'Weighted {{value}}',
      pipelineValue: 'Pipeline Value',
      staleDeals: 'Stale deals {{count}}',
      nextFollowUp: 'Next Follow-up',
      overdue: 'Overdue {{count}}',
      openQuotesJobs: 'Open Quotes / Jobs',
      lastTouch: 'Last touch {{date}}',
    },
    actionsTitle: 'AI Next Best Actions',
    actionsDescription: 'Account-specific commercial recommendations synthesized from follow-ups, deal freshness, quotations, and active jobs.',
    actionsEmpty: 'AI does not see an urgent next commercial action for this account yet.',
    recommendations: {
      loading: 'Loading AI priorities...',
      due: 'Due {{date}}',
      score: 'Score {{score}}',
      openAccount360: 'Open Account 360',
      priority: {
        critical: 'Critical',
        high: 'High',
        medium: 'Medium',
        low: 'Low',
      },
      summary: {
        critical: '{{count}} critical',
        high: '{{count}} high',
        medium: '{{count}} medium',
      },
    },
  },

  operationalIntelligence: {
    workspaceNotFound: 'Workspace not found',
    dataRecords: {
      title: 'Operational Registers',
      description: 'Maintain service, incident, warranty, inventory, safety, fleet, and signal registers as editable working tables.',
      loadFailed: 'Failed to load datasets',
      importFailed: 'Failed to import dataset',
      metrics: {
        datasets: 'Registers',
        records: 'Rows',
        uploadMode: 'Upload Mode',
      },
      uploadModes: {
        replace: 'Replace dataset',
        append: 'Append dataset',
      },
      actions: {
        quickCapture: 'Quick Capture',
        documents: 'Documents & Evidence',
        refresh: 'Refresh Data',
      },
      guidance: {
        title: 'Register guidance',
        description: 'Guidance is hidden by default to keep the workspace clean.',
      },
      datasetCard: {
        records: '{{count}} records',
        fields: '{{count}} fields',
        sourceEvidence: 'Source evidence: {{date}}',
        structure: 'Dataset Structure',
        latestSourceEvidence: 'Latest source evidence',
        storedAt: 'Stored in the shared OPI document workspace on {{date}}',
        openSourceFile: 'Open Source File',
        openLiveRecords: 'Open Live Records',
        addRow: 'Add Row',
        importing: 'Importing...',
        uploadCsv: 'Upload CSV',
      },
    },
    documents: {
      title: 'Documents & Evidence',
      description: 'Upload notes, photos, complaint files, warranty evidence, and source spreadsheets into the shared Operational Intelligence document workspace.',
      workspaceTitle: 'Operational Intelligence document workspace',
      workspaceDescription: 'This page keeps uploaded evidence live and reusable across cases, signals, and source-data imports.',
      backToSourceData: 'Back To Registers',
      uploadEnabled: 'Upload enabled',
      summary: {
        aiReady: 'AI Ready',
        aiReadyDescription: 'visible documents with AI summary or interpreted purpose',
        draftLinked: 'Draft Linked',
        draftLinkedDescription: 'visible documents that already created or linked a live draft',
        linkedRecords: 'Linked Records',
        linkedRecordsDescription: 'cross-links on the current file list into incidents, signals, cases, and evidence chains',
        pendingReview: 'Pending Review',
        pendingReviewDescription: 'visible documents still pending completion or further operator review',
      },
      whyThisViewExists: 'Why this view exists',
      intro: 'Documents are not passive attachments here. Each file can carry parse status, AI summary, suggested routing, and linked records across Operational Intelligence workflows.',
      badges: {
        parseStatus: 'Parse status shown inline',
        aiSummary: 'AI doc type + AI summary shown inline',
        suggestedRoute: 'Suggested route shown inline',
        linkedRecords: 'Linked records shown inline',
      },
    },
    quickCapture: {
      title: 'Quick Capture',
      description: 'Capture frontline signals in real time. AI will triage the record, route it into the correct operational flow, and trigger alerts when needed.',
      loadStatsFailed: 'Failed to load signal stats',
      requiredFields: 'Category, site, and subject are required.',
      captureFailed: 'Failed to capture signal',
      formTitle: 'Frontline Capture Form',
      formDescription: 'AI will classify the signal, draft a summary, and trigger alerts when thresholds are met.',
      openSignalIntake: 'Open Signal Intake',
      pushEnabled: 'Push Enabled',
      enablePushAlerts: 'Enable Push Alerts',
      stats: {
        total: 'Captured Signals',
        open: 'Open Signals',
        escalated: 'Escalated',
      },
      attachments: 'Attachments',
      trackedWorkspaceFiles: 'Tracked workspace files',
      storedInWorkspace: 'Stored in the shared Operational Intelligence document workspace',
      open: 'Open',
      guidance: {
        title: 'Capture guidance',
        description: 'Guidance is hidden by default. Expand only when you need quick reminders.',
      },
      sourceData: 'Registers',
      submitForAiTriage: 'Submit For AI Triage',
      latestCaptureReceipt: 'Latest Capture Receipt',
      linkedThroughEvidenceChain: 'Linked through the shared workspace evidence chain',
      viewFile: 'View File',
      noFileEvidence: 'This signal was submitted without file evidence.',
      openCreatedRecord: 'Open created record',
      emptyReceipt: 'Submit a signal once and the latest captured file evidence will stay visible here for traceability.',
      categoryMix: 'Category Mix',
      recentSignals: 'Recent Signals',
      noSignals: 'No signals captured yet.',
      pushHintEnabled: 'Push alerts are enabled on this device. High-priority signal escalations can land here immediately.',
      pushHintDisabled: 'Push alerts are not enabled on this device yet. Enable them above for live operational follow-up.',
    },
  },

  // ── Today page ──
  today: {
    title: 'Hari Ini',
    tasks: 'tugas',
    allClear: 'Tiada tugasan hari ini!',
    allClearDesc: 'Tiada tugas, kerja, atau penghantaran dijadualkan untuk hari ini.',
    jobTitle: '{{module}} #{{id}}',
    tripTitle: 'Perjalanan {{id}}',
    wmsTaskTitle: 'WMS {{type}} #{{id}}',
    shipmentTitle: '{{type}} {{id}}',
  },

  // ── Module labels (standalone) ──
  module: {
    hms: 'Komando Haulage',
    tms: 'Pengangkutan Klasik',
    wms: 'Aliran Gudang',
    fms: 'Pilot Kargo',
    scm: 'Navigator Rantaian Bekalan',
    svc: 'Perkhidmatan',
    hrm: 'Tenaga Kerja',
    oms: 'Pesanan',
    acc: 'Perakaunan',
  },

  // ── Common ──
  common: {
    loading: 'Memuatkan...',
    viewAll: 'Lihat semua',
    more: 'lagi',
    save: 'Simpan',
    reset: 'Tetapkan semula',
  },

  // ── Settings page ──
  settings: {
    desc: 'Peribadikan pengalaman Boox anda.',
    tabs: 'Tab',
    clearTabsOnSignout: 'Kosongkan tab semasa log keluar',
    clearTabsOnSignoutDesc: 'Apabila diaktifkan, semua tab terbuka akan ditutup semasa anda log keluar. Nyahaktifkan untuk mengekalkan tab anda untuk sesi seterusnya.',
    clearTabsEnabled: 'Tab akan dikosongkan semasa log keluar',
    clearTabsDisabled: 'Tab akan dikekalkan merentas sesi',
    voiceCommands: 'Arahan Suara',
    voiceCommandsAlwaysOn: 'Arahan suara PWA sentiasa aktif',
    voiceCommandsAlwaysOnDesc: 'Menambah togol mikrofon pada bar sisi PWA dan terus mendengar selepas setiap arahan sehingga anda mematikannya.',
    voiceCommandsEnabled: 'Arahan suara PWA diaktifkan',
    voiceCommandsDisabled: 'Arahan suara PWA dinyahaktifkan',
    langHint: 'Digunakan serta-merta di seluruh aplikasi.',
    surfaceMenu: 'Menu Permukaan Peribadi',
    surfaceMenuDesc: 'Pilih kategori menu yang dipaparkan untuk anda dalam PWA dan sambungan Chrome.',
    surfaceSaved: 'Menu permukaan peribadi disimpan',
    surfaceSaveFailed: 'Gagal menyimpan menu peribadi',
    surfaceReset: 'Menu permukaan peribadi ditetapkan semula',
    surfaceResetFailed: 'Gagal menetapkan semula menu peribadi',
    pwaMenu: 'Menu PWA',
    pwaMenuDesc: 'Menu peribadi semasa digunakan dalam go-pwa.',
    extensionMenu: 'Menu Sambungan',
    extensionMenuDesc: 'Menu peribadi semasa digunakan dalam sambungan Chrome.',
    categories: 'kategori',
  },

  // ── Accent color ──
  accent: {
    label: 'Warna Aksen',
  },

  // ── Sidebar ──
  sidebar: {
    collapse: 'Runtuhkan',
    expand: 'Kembangkan',
  },

  // ── MDI Tabs ──
  mdi: {
    closeTab: 'Tutup Tab',
    closeOther: 'Tutup Lain',
  },

  // ── Theme ──
  theme: {
    label: 'Tema',
    languageLabel: 'Bahasa',
    light: 'Cerah',
    dark: 'Gelap',
    system: 'Sistem',
  },

  // ── Forgot Password ──
  forgotPassword: {
    title: 'Lupa Kata Laluan',
    desc: 'Masukkan e-mel anda dan kami akan menghantar pautan set semula.',
    submit: 'Hantar Pautan Set Semula',
    sending: 'Menghantar…',
    sentTitle: 'Semak E-mel Anda',
    sentDesc: 'Kami telah menghantar pautan set semula kata laluan ke {{email}}.',
    resend: 'Hantar Semula E-mel',
    backToLogin: 'Kembali ke Log Masuk',
    failed: 'Gagal menghantar e-mel set semula',
  },

  // ── Reset Password ──
  resetPassword: {
    title: 'Set Semula Kata Laluan',
    desc: 'Masukkan kata laluan baru anda di bawah.',
    newPassword: 'Kata Laluan Baru',
    confirmPassword: 'Sahkan Kata Laluan',
    minLength: 'Minimum 8 aksara',
    mismatch: 'Kata laluan tidak sepadan',
    tooShort: 'Kata laluan mestilah sekurang-kurangnya 8 aksara',
    submit: 'Set Semula Kata Laluan',
    resetting: 'Menetapkan semula…',
    successTitle: 'Kata Laluan Ditetapkan Semula',
    successDesc: 'Kata laluan anda telah berjaya ditetapkan semula. Mengalihkan ke log masuk…',
    goToLogin: 'Ke Log Masuk',
    failed: 'Gagal menetapkan semula kata laluan',
    invalidLink: 'Pautan Set Semula Tidak Sah',
    invalidLinkDesc: 'Pautan set semula ini tidak sah atau telah tamat tempoh.',
    requestNew: 'Minta Pautan Baharu',
  },

  // ── Two-Factor Authentication ──
  twoFactor: {
    title: 'Pengesahan Dua Faktor',
    emailDesc: 'Masukkan kod 6 digit yang dihantar ke {{email}}.',
    appDesc: 'Masukkan kod 6 digit daripada aplikasi pengesah anda.',
    verify: 'Sahkan',
    verifying: 'Mengesahkan…',
    invalidCode: 'Kod pengesahan tidak sah',
    resend: 'Hantar Semula Kod',
    resendIn: 'Hantar semula dalam {{seconds}}s',
    backToLogin: 'Kembali ke Log Masuk',
  },

  // ── Admin panel ──
  admin: {
    title: 'Pengurusan AI',
    groupOps: 'Operasi',
    groupSkills: 'Kemahiran & Alatan',
    groupTraining: 'Latihan AI',
    groupConfig: 'Konfigurasi Platform',
    domainConfigs: 'Konfigurasi Domain',
    menuConfig: 'Konfigurasi Menu',
    userRoles: 'Pengguna & Peranan',
    commandCenter: 'Pusat Perintah',
    brainMonitor: 'Monitor Otak',
    brainSoul: 'Brain Soul',
    brainSchedule: 'Jadual Brain',
    jobTimeline: 'Garis Masa Job',
    agentLab: 'AgentLab',
    aiTeam: 'Pasukan AI',
    logViewer: 'Pemapar Log',
    alertsApprovals: 'Amaran & Kelulusan',
    skillManager: 'Pengurus Kemahiran',
    skillResources: 'Sumber Kemahiran',
    toolRegistry: 'Daftar Alatan',
    promptEditor: 'Editor Prompt',
    trainingData: 'Data Latihan',
    memoryManager: 'Pengurus Memori',
    docTemplates: 'Templat Dokumen',
    configVersions: 'Versi Konfigurasi',
    schemaEditor: 'Editor Skema',
    workflows: 'Aliran Kerja',
    scriptEditor: 'Editor Skrip',
    search: 'Cari',
    refresh: 'Muat Semula',
    create: 'Cipta',
    save: 'Simpan',
    cancel: 'Batal',
    delete: 'Padam',
    edit: 'Edit',
    preview: 'Pratonton',
    copy: 'Salin',
    all: 'Semua',
    required: 'Diperlukan',
    optional: 'Pilihan',
    noData: 'Tiada data',
    prompt: {
      listTitle: 'Senarai Prompt',
      searchPlaceholder: 'Cari prompt…',
      noMatch: 'Tiada prompt yang sepadan',
      selectHint: 'Pilih prompt dari kiri untuk mula mengedit',
      active: 'Aktif',
      noActiveVersion: 'Tiada versi aktif',
      unsavedChanges: 'Perubahan belum disimpan',
      params: 'Parameter',
      history: 'Sejarah',
      rollback: 'Kembalikan',
      saveNewVersion: 'Simpan Versi Baharu',
      targetModel: 'Model Sasaran',
      maxTokens: 'Token Maks',
      temperature: 'Suhu',
      variables: 'Pemboleh ubah (koma-pisah)',
      editorPlaceholder: 'Tulis kandungan prompt di sini…',
      changeNote: 'Nota perubahan (pilihan)…',
      chars: 'aksara',
      tokens: 'token',
      versionHistory: 'Sejarah Versi',
      noVersions: 'Tiada rekod versi',
      draft: 'Draf',
      archived: 'Diarkibkan',
      load: 'Muatkan',
      activate: 'Aktifkan',
      uses: '{{count}} penggunaan',
      quality: 'Kualiti {{score}}%',
      emailParsingSystem: '📧 Penghuraian E-mel (Sistem)',
      emailParsingUser: '📧 Penghuraian E-mel (Pengguna)',
      jobCreationSystem: '🚚 Penciptaan Kerja (Sistem)',
      jobCreationUser: '🚚 Penciptaan Kerja (Pengguna)',
      jobValidationSystem: '✅ Pengesahan Kerja (Sistem)',
      hsCodeSystem: '📦 Kod HS (Sistem)',
      billingSystem: '💰 Pengebilan (Sistem)',
      customerInquirySystem: '💬 Pertanyaan Pelanggan (Sistem)',
      seaImportSystem: '🚢 Import Laut (Sistem)',
      nvoccSystem: '📋 Penyatuan NVOCC (Sistem)',
      dgValidationSystem: '⚠️ Pengesahan DG (Sistem)',
      complianceSystem: '🔒 Semakan Pematuhan (Sistem)',
      starterTemplate: 'Templat permulaan — edit dan simpan sebagai v1',
      agentRouter: '🧭 Ejen Penghala',
      agentDev: '💻 Ejen Pembangunan',
      agentOps: '⚙️ Ejen Operasi',
      agentData: '📊 Ejen Data',
      agentCoordinator: '🔗 Ejen Penyelaras',
      agentSafety: '🛡️ Ejen Keselamatan',
      agentInbox: '📬 Ejen Peti Masuk',
      agentBilling: '💳 Ejen Pengebilan',
      agentCs: '🎧 Ejen CS',
      providerAnthropic: '🟠 Anthropic (Claude)',
      providerOpenai: '🟢 OpenAI (GPT-4)',
      providerGemini: '🔵 Google (Gemini)',
      providerDefault: '⚪ Pembekal Lalai',
      safetyDatabase: '🗄️ Peraturan Keselamatan DB',
      safetyTenantIsolation: '🔒 Peraturan Pengasingan Penyewa',
      safetySql: '🛡️ Peraturan Keselamatan SQL',
      autonomousBehavior: '🤖 Tingkah Laku Autonomi',
      docParserMbl: '📄 Penghurai MBL',
      docParserHbl: '📄 Penghurai HBL',
      docParserManifest: '📋 Penghurai Manifes',
      docParserNoa: '📢 Penghurai NOA',
    },
    skill: {
      catalog: 'Katalog Sumber Kemahiran',
      searchPlaceholder: 'Cari kemahiran…',
      noData: 'Tiada data kemahiran',
      selectHint: 'Pilih kemahiran untuk melihat konfigurasi sumber',
      resources: 'sumber',
      customPoints: 'titik khusus',
      resourceBinding: 'Pengikatan Sumber',
      customization: 'Penyesuaian',
      runtimePreview: 'Pratonton Runtime',
      declared: 'diisytiharkan',
      bound: 'terikat',
      isBound: 'Terikat',
      notBound: 'Tidak Terikat',
    },
    training: {
      title: 'Pengurusan Data Latihan',
      tabExamples: 'Contoh Latihan',
      tabEmbeddings: 'Pembenaman',
      tabPreferences: 'Keutamaan Pelanggan',
      searchExamples: 'Cari contoh…',
      newExample: 'Contoh Baharu',
      total: '{{count}} jumlah',
      golden: 'Emas',
      approved: 'Diluluskan',
      pending: 'Menunggu',
      rejected: 'Ditolak',
      sampleKey: 'kunci-sampel (ID unik)',
      description: 'Penerangan (pilihan)',
      inputLabel: 'Input (JSON atau teks)',
      outputLabel: 'Output Dijangka (JSON atau teks)',
      noExamples: 'Tiada contoh latihan',
      qualityLabel: 'Kualiti',
    },
    memory: {
      title: 'Pengurus Memori',
      subtitle: 'Seni bina memori 3 lapisan',
      taskMemory: 'Memori Tugas',
      workspaceMemory: 'Memori Ruang Kerja',
      orgMemory: 'Memori Organisasi',
      searchPlaceholder: 'Cari kunci atau kandungan memori…',
      records: '{{count}} rekod',
      noRecords: 'Tiada rekod memori',
      noRecordsHint: 'Entri memori dicipta secara automatik apabila Ejen berjalan',
      expired: 'Tamat tempoh',
      latest: 'Terkini',
      value: 'Nilai',
      metadata: 'Metadata',
    },
    docTemplate: {
      title: 'Pengurusan Templat Dokumen',
      templates: '{{count}} templat',
      newTemplate: 'Templat Baharu',
      searchPlaceholder: 'Cari templat…',
      invoiceTemplate: 'Templat Invois',
      quotationTemplate: 'Templat Sebut Harga',
      blTemplate: 'Templat BL',
      doTemplate: 'Templat DO',
      soaTemplate: 'Templat SOA',
      emailTemplate: 'Templat E-mel',
      receiptTemplate: 'Templat Resit',
      manifestTemplate: 'Templat Manifes',
      newDocTemplate: 'Templat Dokumen Baharu',
      skillId: 'ID Kemahiran',
      resourceKey: 'Kunci Sumber',
      type: 'Jenis',
      format: 'Format',
      noTemplates: 'Tiada templat dokumen',
      noTemplatesHint: 'Klik "Templat Baharu" untuk menambah templat dokumen kemahiran',
    },
    tool: {
      title: 'Daftar Alatan',
      toolsSkills: '{{count}} alatan/kemahiran',
      newDynamicSkill: 'Kemahiran Dinamik Baharu',
      searchPlaceholder: 'Cari kemahiran/alatan…',
      nativeLabel: 'Asli',
      dynamicLabel: 'Dinamik',
      promptDriven: 'Dipacu Prompt',
      toolComposed: 'Terdiri daripada Alatan',
      sandboxBundle: 'Skrip Kotak Pasir',
      promptDrivenDesc: 'LLM menjana output berstruktur melalui templat prompt',
      toolComposedDesc: 'Susun rantaian ID alatan sedia ada untuk pelaksanaan',
      sandboxBundleDesc: 'Kotak pasir Python/JS melaksanakan logik tersuai',
      slug: 'ID (slug)',
      name: 'Nama',
      riskLevel: 'Tahap Risiko',
      descriptionLabel: 'Penerangan',
      intents: 'Niat (koma-pisah)',
      promptKey: 'Kunci Prompt',
      toolIdChain: 'Rantaian ID alatan (koma-pisah)',
      model: 'Model',
      dynamicSkills: 'Kemahiran Dinamik',
      nativeSkills: 'Kemahiran Asli',
      enabled: 'Diaktifkan',
      disabled: 'Dilumpuhkan',
      native: 'Asli',
      dynamic: 'Dinamik',
      intentsLabel: 'Niat',
      permissions: 'Kebenaran',
      none: 'Tiada',
      toolChain: 'Rantaian Alatan',
      retry: 'Cuba Semula',
      escalation: 'Eskalasi',
      version: 'Versi',
      noData: 'Tiada alatan/kemahiran',
    },
    configVer: {
      title: 'Pengurusan Versi Konfigurasi',
      versions: '{{count}} versi',
      defaultTemplate: 'Templat Lalai',
      editPublish: 'Edit & Terbitkan',
      finalChannel: 'Saluran Akhir',
      experimentalChannel: 'Saluran Eksperimen',
      currentConfig: 'Konfigurasi Aktif Semasa',
      defaultLabel: 'Lalai',
      cached: 'Dicache',
      tenant: 'Penyewa',
      layoutVersion: 'Versi Susun Atur',
      publishTime: 'Masa Terbit',
      signature: 'Tandatangan',
      menuItems: 'Item Menu',
      pages: 'Halaman',
      featureFlags: 'Bendera Ciri',
      noConfig: 'Tiada konfigurasi',
      publish: 'Terbitkan',
      publishing: 'Menerbitkan…',
      rollbackTo: 'Kembalikan ke versi ini',
      versionHistory: 'Sejarah Versi',
      noVersionHistory: 'Tiada sejarah versi',
      latest: 'Terkini',
      current: 'Semasa',
      publishNewVersion: 'Terbitkan Versi Konfigurasi Baharu',
      publishNote: 'Akan menandatangan secara automatik dan menaikkan versi selepas terbit',
    },
    userPerf: {
      title: 'Analitik Tingkah Laku Pengguna',
      subtitle: 'Penggunaan ciri · Penggunaan AI · Amaran pengguna berisiko',
      lastRefresh: 'Kemas kini terakhir',
      totalUsers: 'Jumlah Pengguna',
      activeUsers: 'Pengguna Aktif',
      activeUsersSub: 'Aktif dalam 7 hari lepas',
      riskUsers: 'Pengguna Berisiko ⚠',
      riskUsersSub: '7+ hari tidak aktif',
      avgAiAdoption: 'Purata Penggunaan AI',
      avgAiAdoptionSub: 'Operasi AI / jumlah operasi',
      totalOps: 'Jumlah Operasi',
      featureHeat: 'Top 5 Penggunaan Ciri',
      recentEvents: 'Peristiwa Perniagaan Terkini',
      aiDistribution: 'Taburan Penggunaan AI',
      aiHigh: 'Tinggi ≥20%',
      aiMid: 'Sederhana 5-20%',
      aiLow: 'Rendah <5%',
      userTable: 'Butiran Prestasi Pengguna',
      tableSortHint: '(klik lajur untuk isih)',
      colUser: 'Pengguna',
      colTotalOps: 'Jumlah Ops',
      colFeatures: 'Ciri Digunakan',
      colActiveDays: 'Hari Aktif',
      colAiAdoption: 'Penggunaan AI',
      colLastActive: 'Terakhir Aktif',
      colStatus: 'Status',
      statusActive: 'Aktif',
      statusInactive: '⚠ 7h tidak aktif',
      noData: 'Tiada data',
      days: '{{count}} hari',
      activeDays: '{{count}} h',
      periodPrefix: 'Lepas',
      justNow: 'Baru sahaja',
      minutesAgo: '{{count}}m lalu',
      hoursAgo: '{{count}}j lalu',
      daysAgo: '{{count}}h lalu',
      last7: '7 hari lepas',
      last14: '14 hari lepas',
      last30: '30 hari lepas',
      last60: '60 hari lepas',
    },
  },

  flm: {
    page: {
      dashboard: 'Papan Pemuka Fleet',
      assets: 'Kenderaan',
      workshop: 'Perintah Kerja',
      parts: 'Inventori Alat Ganti',
      tyres: 'Tayar',
      costing: 'Kos Fleet',
      reports: 'Laporan Fleet',
    },
    desc: {
      dashboard: 'Pantau ketersediaan kenderaan, beban bengkel, amaran alat ganti, status tayar dan kos fleet bulan ini.',
      assets: 'Rekod aset fleet untuk lori, van, prime mover, trailer, chassis dan peralatan sokongan.',
      workshop: 'Permintaan servis dan work order pembaikan dengan masa henti dan kos.',
      parts: 'Stok alat ganti bengkel, tahap pesanan semula, kos unit dan sejarah pengeluaran.',
      tyres: 'Kitaran hayat tayar daripada stok kepada dipasang, dibaiki, retread dan lupus.',
      costing: 'Peruntukan kos penyelenggaraan, tayar, minyak dan kos tetap mengikut kenderaan dan tempoh.',
      reports: 'Kos per km, masa henti, pergerakan stok, hayat tayar dan laporan pengecualian pematuhan.',
    },
    action: { refresh: 'Segar semula', create: 'Cipta', record: 'Rekod', close: 'Tutup', send: 'Hantar', notifyDriver: 'Maklum Pemandu', resend: 'Hantar Semula' },
    error: { load: 'Gagal memuatkan data FLM', save: 'Gagal menyimpan tindakan FLM' },
    loading: 'Memuatkan rekod FLM...',
    empty: 'Tiada rekod {{label}} lagi. Asas FLM sedia untuk data penyewa.',
    table: {
      vehicles: 'kenderaan',
      workOrders: 'work order',
      parts: 'alat ganti',
      tyres: 'tayar',
      costing: 'peruntukan kos',
    },
    col: {
      plate: 'No. Plat',
      type: 'Jenis',
      makeModel: 'Jenama / Model',
      status: 'Status',
      odometer: 'Odometer',
      workOrder: 'Work Order',
      vehicle: 'Kenderaan',
      cost: 'Kos',
      part: 'Alat Ganti',
      code: 'Kod',
      stock: 'Stok',
      reorder: 'Pesan Semula',
      unitCost: 'Kos Unit',
      serial: 'Siri',
      size: 'Saiz',
      position: 'Posisi',
      date: 'Tarikh',
      category: 'Kategori',
      source: 'Sumber',
      amount: 'Amaun',
      action: 'Tindakan',
    },
    form: {
      assetTitle: 'Cipta Kenderaan',
      serviceRequestTitle: 'Log Permintaan Servis',
      workOrderTitle: 'Cipta Work Order',
      partTitle: 'Cipta Alat Ganti',
      stockTitle: 'Rekod Pergerakan Stok',
      tyreTitle: 'Cipta Tayar',
      tyreEventTitle: 'Rekod Peristiwa Tayar',
      fuelTitle: 'Rekod Kos Minyak',
      complianceTitle: 'Rekod Pematuhan',
      notifyDriverTitle: 'Maklumkan Pemandu',
      plateNo: 'No. plat',
      assetName: 'Nama aset',
      assetType: 'Jenis aset',
      odometer: 'Odometer km',
      vehicle: 'Kenderaan',
      selectVehicle: 'Pilih kenderaan',
      serviceRequest: 'Permintaan servis',
      selectServiceRequest: 'Pilih permintaan servis',
      requestType: 'Jenis permintaan',
      workType: 'Jenis kerja',
      priority: 'Keutamaan',
      vendorCost: 'Kos vendor',
      miscCost: 'Kos lain',
      diagnosis: 'Diagnosis / skop',
      description: 'Penerangan',
      partCode: 'Kod alat ganti',
      partName: 'Nama alat ganti',
      qty: 'Kuantiti',
      reorder: 'Tahap pesan semula',
      unitCost: 'Kos unit',
      part: 'Alat ganti',
      selectPart: 'Pilih alat ganti',
      movement: 'Pergerakan',
      workOrder: 'Work order',
      selectWorkOrder: 'Pilih work order',
      serialNo: 'No. siri',
      brand: 'Jenama',
      tyreSize: 'Saiz tayar',
      purchaseCost: 'Kos belian',
      tyre: 'Tayar',
      selectTyre: 'Pilih tayar',
      eventType: 'Jenis peristiwa',
      position: 'Posisi',
      costAmount: 'Amaun kos',
      litres: 'Liter',
      unitPrice: 'Harga unit',
      vendor: 'Vendor',
      complianceType: 'Jenis pematuhan',
      expiryDate: 'Tarikh luput',
      complianceStatus: 'Status pematuhan',
      notificationType: 'Jenis notifikasi',
      amount: 'Amaun',
      note: 'Nota',
    },
    success: {
      asset: 'Kenderaan dicipta',
      serviceRequest: 'Permintaan servis dicipta',
      workOrder: 'Work order dicipta',
      part: 'Alat ganti dicipta',
      stock: 'Pergerakan stok direkod',
      tyre: 'Tayar dicipta',
      tyreEvent: 'Peristiwa tayar direkod',
      fuel: 'Kos minyak direkod',
      compliance: 'Rekod pematuhan dicipta',
      notifyDriver: 'Notifikasi pemandu dihantar',
      resendDriver: 'Notifikasi pemandu dihantar semula',
      close: 'Work order ditutup',
    },
    assetType: {
      lorry: 'Lori',
      van: 'Van',
      prime_mover: 'Prime mover',
      trailer: 'Trailer',
      forklift: 'Forklift',
      other: 'Lain-lain',
    },
    workType: {
      service: 'Servis',
      repair: 'Baiki',
      breakdown: 'Rosak',
      tyre: 'Tayar',
      inspection: 'Pemeriksaan',
      other: 'Lain-lain',
    },
    requestType: {
      defect: 'Kerosakan',
      service_due: 'Servis perlu',
      breakdown: 'Rosak',
      tyre: 'Tayar',
      compliance: 'Pematuhan',
      other: 'Lain-lain',
    },
    notificationType: {
      repair_completed: 'Baiki selesai',
      extra_cost_settlement: 'Penyelesaian kos tambahan',
      workshop_update: 'Kemaskini bengkel',
      general: 'Mesej umum',
    },
    priority: {
      low: 'Rendah',
      normal: 'Normal',
      high: 'Tinggi',
      urgent: 'Segera',
    },
    movement: {
      receive: 'Terima',
      issue: 'Keluar ke work order',
      adjust_in: 'Pelarasan masuk',
      adjust_out: 'Pelarasan keluar',
      return: 'Pulangan',
    },
    tyreEvent: {
      fit: 'Pasang',
      remove: 'Tanggal',
      retread: 'Retread',
      scrap: 'Lupus',
      inspect: 'Periksa',
    },
    kpi: {
      assets: 'Aset fleet',
      available: '{{count}} tersedia',
      blocked: 'Kenderaan disekat',
      blockedHint: 'Bengkel, rosak atau pegangan pematuhan',
      openWorkOrders: 'Work order terbuka',
      lowStockParts: '{{count}} alat ganti stok rendah',
      monthCost: 'Kos bulan ini',
      fittedTyres: '{{count}} tayar dipasang',
    },
    status: {
      active: 'Aktif',
      workshop: 'Bengkel',
      breakdown: 'Rosak',
      compliance_hold: 'Pegangan pematuhan',
      off_road: 'Tidak beroperasi',
      sold: 'Dijual',
      draft: 'Draf',
      open: 'Terbuka',
      in_progress: 'Sedang berjalan',
      waiting_parts: 'Menunggu alat ganti',
      vendor_work: 'Kerja vendor',
      completed: 'Selesai',
      closed: 'Ditutup',
      cancelled: 'Dibatalkan',
      in_stock: 'Dalam stok',
      fitted: 'Dipasang',
      repair: 'Baiki',
      retread: 'Retread',
      scrapped: 'Dilupuskan',
      expiring: 'Akan luput',
      hold: 'Pegangan',
      expired: 'Luput',
      waived: 'Dikecualikan',
      triaged: 'Ditapis',
      approved: 'Diluluskan',
      waiting_driver: 'Menunggu pemandu',
    },
    panel: {
      recentRequests: 'Permintaan servis terkini',
      complianceAlerts: 'Amaran pematuhan',
      driverNotifications: 'Log WhatsApp pemandu',
    },
    next: {
      title: 'Fasa pelaksanaan seterusnya',
      workshopTitle: 'MVP Perintah Kerja',
      workshopText: 'Cipta, kira kos dan tutup work order pembaikan.',
      inventoryTitle: 'Alat ganti dan tayar',
      inventoryText: 'Keluarkan alat ganti dan jejak pemasangan, rotation, pembaikan dan lupus tayar.',
      dispatchTitle: 'Isyarat dispatch',
      dispatchText: 'Sekat aset fleet yang tidak tersedia daripada tugasan TMS/HMS/SVC.',
    },
  },

  tenantAdmin: {
    title: 'Pengurusan Penyewa',
    subtitle: 'Urus modul, pelan & akses penyewa',
    back: 'Kembali',
    edit: 'Edit',
    save: 'Simpan',
    cancel: 'Batal',
    refresh: 'Muat Semula',
    core: 'Teras',
    saveChanges: 'Simpan Perubahan',
    authControl: 'Kawalan Auth',
    connectorSetup: 'Tetapan Connector',
    skillPolicies: 'Polisi Kemahiran',
    manageConnectors: 'Urus Connector',
    managePolicies: 'Urus Polisi',
    tenantInfo: 'Maklumat Penyewa',
    operationalModules: 'Modul Operasi',
    modulesDesc: 'Kawal modul yang boleh diakses oleh penyewa ini. Modul teras (bertanda "Teras") sentiasa diaktifkan.',
    tenantMembers: 'Ahli Penyewa',
    loading: 'Memuatkan…',
    loadingTenants: 'Memuatkan penyewa…',
    loadingConnectors: 'Memuatkan connector…',
    noMembers: 'Tiada ahli',
    noConnectors: 'Tiada connector dikonfigurasi lagi.',
    noTenantsMatch: 'Tiada penyewa sepadan dengan penapis',
    searchPlaceholder: 'Cari mengikut nama atau slug…',
    allTenants: 'Semua Penyewa',
    logisticsTenants: 'Penyewa Logistik (Tanpa FMS)',
    freightTenants: 'Penyewa Kargo (Dengan FMS)',
    fields: {
      name: 'Nama', slug: 'Slug', plan: 'Pelan', status: 'Status',
      currency: 'Mata Wang', locale: 'Lokasi', timezone: 'Zon Masa',
      country: 'Negara', taxId: 'ID Cukai', companyRegNo: 'No Pendaftaran Syarikat',
      businessDesc: 'Keterangan Perniagaan',
    },
    placeholders: {
      taxId: 'cth. No Pendaftaran GST', companyRegNo: 'cth. UEN 201234567M',
      businessDesc: 'Keterangan ringkas perniagaan syarikat…',
    },
    moduleCategory: {
      business: 'Pengurusan Perniagaan',
      vertical: 'Modul Industri',
      integration: 'Integrasi & Connector',
    },
    modules: {
      OMS: 'Pesanan (OMS)', CRM: 'Relationship Management', INBOX: 'Peti Masuk (INBOX)', AIC: 'Sembang AI (AIC)', OPI: 'Risikan Operasi (OPI)', AI: 'AI Agentic Control (AI)', TA: 'Task Management (TA)', RM: 'Relationship Management (RM)', TM: 'Transport Hero (TM)', TP: 'Transport Planning (TP)', CT: 'Trak Kontena (CT)', WM: 'Pengurusan Gudang (WM)', CF: 'Gudang CFS (CF)', FM: 'Pengurusan Kargo (FM)', SC: 'Supply Chain (SC)', MT: 'Maritime & Port Agency (MT)', TMS: 'Pengangkutan (TMS)',
      FLM: 'Pengurusan Fleet (FLM)',
      HMS: 'Trak Kontena (HMS)', DMS: 'Depot (DMS)', FMS: 'Kargo (FMS)', SAS: 'Agensi Perkapalan (SAS)', WMS: 'Gudang (WMS)',
      HRM: 'Tenaga Kerja (HRM)', ACC: 'Perakaunan (ACC)', SET: 'Tetapan (SET)', PRO2: 'Pelayar Legasi PRO2',
      SCM: 'Rantaian Bekalan (SCM)',
      SVC: 'Pengurusan Perkhidmatan (SVC)',
      SOC: 'Kontena Milik Pengirim (SOC)',
      CW: 'Connector CargoWise',
      CERP: 'Connector CargoERP',
      EZSH: 'Connector EZShip',
      SFRT: 'Connector SysFreight',
      KDCFS: 'Connector KDCFS',
      QB: 'Connector QuickBooks',
      XERO: 'Connector Xero',
      ORCL: 'Connector Oracle ERP',
      SAP: 'Connector SAP',
      NETSUITE: 'Connector NetSuite',
      FR8: 'Connector FR8LAN',
      FLINK: 'Connector FreightLink',
      KFLD: 'Connector Keyfield',
      OPTTRK: 'Connector Opt-E-Truck',
      OPTHUB: 'Connector Opt-E-Hub',
    },
    moduleDesc: {
      OMS: 'Perniagaan teras: pesanan, sebut harga, saluran paip, pelanggan',
      CRM: 'Relationship intelligence: pipeline, kenalan, Account 360, pemulihan, dan isyarat portal layan diri',
      INBOX: 'Mesej, e-mel, WhatsApp, tugas, kelulusan',
      AIC: 'Sembang copilot AI, panel sisi, dan ruang kerja perbualan ejen',
      OPI: 'Ruang risikan operasi AI: analisis prestasi, insiden, isyarat perkhidmatan, anomali inventori, EHS dan ringkasan pengurusan',
      AI: 'SDK-governed agentic control: AI workbench, prompt profiles, approvals, context and audit',
      TA: 'SDK menu-set for tasks, actions, schedules, attachments, assignment and reports',
      RM: 'Relationship Management: account, contact, supplier, partner, and service relationship layer',
      TM: 'Profil pengangkutan dinamik baharu: peti masuk, job order, trips, billing, planner, tugas PWA, perkongsian dan laporan',
      TP: 'AI Agentic native transport planning: planner, constraints, costing, documents and approval-gated AI proposals',
      CT: 'Profil trak kontena dinamik baharu: job order, kontena, trips, pasukan pemandu, tugas PWA, perkongsian, billing dan laporan',
      WM: 'Profil gudang dinamik baharu: penerimaan, inventori, picking, putaway, perancangan sumber mengikut jam, tugas PWA, perkongsian dan laporan',
      CF: 'Profil gudang CFS dinamik baharu: kontena, tally kargo, devanning, stuffing, storan, pelepasan, handoff FM, tugas PWA, perkongsian dan billing',
      FM: 'Profil kargo dinamik baharu: shipment, dokumen, pricing, costing, billing handoff, perkongsian dan laporan',
      SC: 'Supply chain profile: order, supplier, procurement, fulfillment, inventory and document control',
      MT: 'Maritime and port agency profile: vessel, port call, berth, tally, documents, service orders and disbursement',
      TMS: 'Penghantaran tempatan, trak, perancang, penghantaran',
      HMS: 'Kargo FCL, operasi pelabuhan, IMP/RET, COL/EXP',
      FLM: 'Kenderaan, bengkel, alat ganti, tayar, pematuhan dan kos fleet',
      DMS: 'Gerbang Depot, halaman, pemeriksaan, pergerakan, pengebilan',
      FMS: 'Import/eksport laut/udara, CFS, kastam, kadar',
      SAS: 'Operasi agensi perkapalan, meja pelanggan, pelepasan dan koordinasi principal',
      WMS: 'Penerimaan, inventori, keluar, perintah kerja',
      HRM: 'Pekerja, kehadiran, gaji, cuti, layan diri HR',
      ACC: 'AR/AP, catatan jurnal, kod cukai, laporan kewangan',
      SET: 'Tetapan syarikat, pasukan, pengebilan, templat',
      PRO2: 'Pelayar baca sahaja untuk data legasi PRO2/CargoConnect',
      SCM: 'Pesanan jualan, pesanan pembelian, inventori, katalog produk, pemenuhan',
      SVC: 'Pesanan perkhidmatan, pengurusan peralatan/aset, penjadualan krew, kerja tapak',
      SOC: 'Pengurusan kontena milik pengirim — sentiasa aktif',
      CW: 'Integrasi WiseTech CargoWise One / ediEnterprise',
      CERP: 'Jambatan data sistem legasi CargoERP',
      EZSH: 'Jambatan data platform penghantaran EZShip',
      SFRT: 'Jambatan data ERP SysFreight',
      KDCFS: 'Jambatan data sistem CFS KDCFS',
      QB: 'Penyegerakan perakaunan Intuit QuickBooks',
      XERO: 'Penyegerakan perakaunan awan Xero',
      ORCL: 'Integrasi Oracle ERP / NetSuite',
      SAP: 'Integrasi SAP S/4HANA / Business One',
      NETSUITE: 'Integrasi Oracle NetSuite ERP',
      FR8: 'Connector pasaran kargo FR8LAN',
      FLINK: 'Connector tempahan & penjejakan FreightLink',
      KFLD: 'Connector sistem Keyfield Logistics',
      OPTTRK: 'Connector pengurusan kenderaan Opt-E-Truck',
      OPTHUB: 'Connector pengurusan gudang Opt-E-Hub',
    },
    stats: {
      totalJobs: 'Jumlah Kerja', members: 'Ahli',
      lastActive: 'Aktif Terakhir', connectors: 'Connector',
    },
    summary: {
      totalTenants: 'Jumlah Penyewa', active: 'Aktif',
      freight: 'Kargo (FMS)', totalJobs: 'Jumlah Kerja',
    },
    filter: { all: 'Semua', logistics: 'Logistik', freight: 'Kargo' },
    listCols: {
      tenant: 'Penyewa', status: 'Status', plan: 'Pelan',
      modules: 'Modul', jobs: 'Kerja', lastActivity: 'Aktiviti Terakhir',
    },
    connectorCols: {
      connector: 'Connector', type: 'Jenis', endpoint: 'Endpoint',
      policy: 'Polisi', mode: 'Mod', status: 'Status',
    },
    memberCols: {
      name: 'Nama', email: 'E-mel', role: 'Peranan',
      joined: 'Tarikh Sertai', actions: 'Tindakan',
    },
    toast: {
      modulesSaved: 'Modul disimpan',
      modulesSaveFailed: 'Gagal menyimpan modul',
      tenantInfoSaved: 'Maklumat penyewa disimpan',
      tenantInfoSaveFailed: 'Gagal menyimpan maklumat penyewa',
      memberUpdated: 'Ahli dikemas kini',
      memberUpdateFailed: 'Gagal mengemas kini ahli',
      switchedTenant: 'Konteks penyewa ditukar ke {{name}}',
      switchFailed: 'Gagal menukar konteks penyewa',
      loadMembersFailed: 'Gagal memuatkan ahli',
    },
  },

  // ── SVC (Pengurusan Perkhidmatan) ──
  svc: {
    status: {
      DRAFT: 'Draf', CONFIRMED: 'Disahkan', SCHEDULED: 'Dijadual', DISPATCHED: 'Dihantar',
      IN_PROGRESS: 'Sedang Berjalan', COMPLETED: 'Selesai', INVOICED: 'Diinvois', CANCELLED: 'Dibatal',
      AVAILABLE: 'Tersedia', IN_USE: 'Sedang Diguna', MAINTENANCE: 'Penyelenggaraan', RETIRED: 'Dilupus',
      ON_DUTY: 'Bertugas', OFF_DUTY: 'Tidak Bertugas', ON_LEAVE: 'Cuti', ACTIVE: 'Aktif', INACTIVE: 'Tidak Aktif',
    },
    serviceType: {
      crane_lift: 'Angkat Kren', rigging: 'Rigging', transport: 'Pengangkutan', installation: 'Pemasangan',
      maintenance: 'Penyelenggaraan', inspection: 'Pemeriksaan', other: 'Lain-lain',
    },
    priority: { low: 'Rendah', normal: 'Biasa', high: 'Tinggi', urgent: 'Segera' },
    equipmentType: {
      crane: 'Kren', forklift: 'Forklift', truck: 'Lori', generator: 'Penjana Kuasa',
      scaffold: 'Perancah', tool_set: 'Set Alatan', other: 'Lain-lain',
    },
    field: {
      customer: 'Pelanggan', site: 'Tapak', serviceType: 'Jenis Perkhidmatan', priority: 'Keutamaan',
      scheduledDate: 'Tarikh Dijadual', estHours: 'Anggaran Jam', description: 'Keterangan', remarks: 'Catatan',
      assetCode: 'Kod Aset', name: 'Nama', type: 'Jenis', capacity: 'Kapasiti', serialNo: 'No. Siri',
      location: 'Lokasi', nextServiceDate: 'Tarikh Servis Seterusnya', code: 'Kod', crewName: 'Nama Krew',
      teamLeader: 'Ketua Pasukan', skills: 'Kemahiran', siteCode: 'Kod Tapak', siteName: 'Nama Tapak',
      contact: 'Hubungan', phone: 'Telefon', postalCode: 'Poskod', address: 'Alamat',
    },
    kpi: { totalOrders: 'Jumlah Pesanan', scheduled: 'Dijadual', inProgress: 'Sedang Berjalan', completed: 'Selesai' },
    tab: {
      recentOrders: 'Pesanan Terkini', activeDispatched: 'Aktif / Dihantar', allOrders: 'Semua Pesanan',
      draft: 'Draf', active: 'Aktif', pendingDispatch: 'Menunggu Penghantaran', dispatched: 'Dihantar',
      allEquipment: 'Semua Peralatan', available: 'Tersedia', maintenance: 'Penyelenggaraan',
      allCrews: 'Semua Krew', allSites: 'Semua Tapak', allInvoices: 'Semua Invois',
      pendingBilling: 'Menunggu Bil', costBreakdown: 'Pecahan Kos', summary: 'Ringkasan',
    },
    col: {
      orderNo: 'No. Pesanan', customer: 'Pelanggan', site: 'Tapak', type: 'Jenis', scheduled: 'Dijadual',
      status: 'Status', crew: 'Krew', date: 'Tarikh', equipment: 'Peralatan', priority: 'Keutamaan',
      estHrs: 'Angg. Jam', created: 'Dicipta', assetCode: 'Kod Aset', name: 'Nama', capacity: 'Kapasiti',
      location: 'Lokasi', nextService: 'Servis Seterusnya', lastService: 'Servis Terakhir', code: 'Kod',
      crewName: 'Nama Krew', teamLeader: 'Ketua Pasukan', members: 'Ahli', skills: 'Kemahiran',
      siteName: 'Nama Tapak', address: 'Alamat', contact: 'Hubungan', hours: 'Jam',
      materials: 'Bahan', total: 'Jumlah', labor: 'Buruh', totalCost: 'Jumlah Kos',
      revenue: 'Hasil', marginPct: 'Margin %', period: 'Tempoh', orders: 'Pesanan',
      completed: 'Selesai', cost: 'Kos', avgHrsJob: 'Purata Jam/Kerja', serialNo: 'No. Siri',
      phone: 'Telefon', postalCode: 'Poskod',
    },
    action: {
      confirm: 'Sahkan', schedule: 'Jadual', dispatch: 'Hantar', startWork: 'Mula Kerja',
      complete: 'Selesai', invoice: 'Invois', cancel: 'Batal',
      cancelConfirm: 'Batal pesanan perkhidmatan ini?',
    },
    page: {
      dashboard: 'Pengurusan Perkhidmatan', orders: 'Pesanan Perkhidmatan', dispatch: 'Penghantaran',
      equipment: 'Peralatan', crew: 'Krew', sites: 'Tapak', billing: 'Bil Perkhidmatan',
      costing: 'Kos Perkhidmatan', reports: 'Laporan Perkhidmatan',
    },
    dialog: {
      newOrder: 'Pesanan Perkhidmatan Baharu', addEquipment: 'Tambah Peralatan', addCrew: 'Tambah Krew', addSite: 'Tambah Tapak',
    },
  },
  chat: {
    searchPlaceholder: 'Cari…',
    newChat: 'Perbualan baharu',
    noSessions: 'Tiada perbualan lagi.\nKlik + untuk bermula.',
    noSearchResults: 'Tiada perbualan yang sepadan',
    archive: 'Arkib',
    delete: 'Padam',
    archivedCount: 'Diarkibkan ({{count}})',
    restore: 'Pulihkan',
    managePrompts: 'Urus Prompt Pantas',
    promptLabel: 'Nama ringkas (cth: 📋 Ringkasan hari ini)',
    promptContent: 'Soalan penuh untuk ditanya kepada AI…',
    autoExecute: 'Hantar serta-merta apabila diklik',
    noPrompts: 'Tiada prompt pantas. Klik "Tambah" untuk mencipta.',
    confirmDelete: 'Adakah anda pasti mahu memadamkan "{{label}}"?',
    deleteFailed: 'Gagal memadamkan perbualan',
    archived: 'Perbualan diarkibkan',
    restored: 'Perbualan dipulihkan',
    chatTitle: 'Sembang',
    thinking: 'Berfikir…',
    share: 'Kongsi perbualan',
    typeToStart: 'Taip di bawah untuk memulakan perbualan',
    startTyping: 'Katakan sesuatu untuk bermula…',
    quickPrompts: 'Prompt Pantas',
    inputPlaceholder: 'Tanya apa sahaja… (Enter untuk hantar, Shift+Enter untuk baris baharu)',
    stopRecording: 'Ketuk untuk berhenti',
    voiceInput: 'Ketuk untuk bercakap',
    stop: 'Hentikan penjanaan',
    disclaimer: 'Respons AI mungkin tidak tepat. Sentiasa sahkan maklumat penting.',
    shareTitle: 'Kongsi Perbualan',
    selectMessages: 'Sertakan mesej',
    shareExpiry: 'Pautan tamat tempoh selepas',
    generateLink: 'Jana Pautan Kongsi',
    copied: 'Disalin!',
    copy: 'Salin',
    sharedConversation: 'Perbualan dikongsi',
    shareWhatsApp: 'Kongsi melalui WhatsApp',
    shareInfo: 'Sesiapa yang mempunyai pautan ini boleh melihat perbualan tanpa log masuk.',
    expiresAt: 'Tamat tempoh',
  },

  finance: {
    method: {
      bank_transfer: 'Pindahan Bank',
      cheque: 'Cek',
      cash: 'Tunai',
      paynow: 'PayNow',
      fps: 'FPS',
      other: 'Lain-lain',
    },
    col: {
      document: 'Dokumen',
      method: 'Kaedah',
      date: 'Tarikh',
      amount: 'Jumlah',
      unallocated: 'Belum Diperuntuk',
      allocated: 'Diperuntukkan',
      status: 'Status',
    },
    kpi: {
      documents: 'Dokumen',
      unallocatedExposure: 'Pendedahan Belum Diperuntuk',
    },
    controls: {
      title: 'Kawalan penyelesaian',
      committedCash: 'Pergerakan tunai yang disahkan',
      allocationOpen: 'Peruntukan masih terbuka',
      unallocatedAmount: 'Jumlah belum diperuntuk',
      requiresApproval: 'Memerlukan kelulusan',
      none: 'Tiada',
      yes: 'Ya',
      no: 'Tidak',
    },
    action: {
      assist: 'Bantuan',
      refresh: 'Muat Semula',
      void: 'Batal',
      reverse: 'Songsang',
      apply: 'Sapukan',
    },
    dock: {
      header: 'Pengepala {{title}}',
      appliedAllocations: 'Peruntukan yang telah digunakan',
      noAllocations: 'Tiada peruntukan lagi.',
      lineBreakdown: 'Pecahan baris pembayaran',
      noLines: 'Tiada pecahan baris.',
      outstandingDocs: 'Dokumen rakan niaga tertunggak',
      noOutstanding: 'Tiada dokumen {{type}} tertunggak untuk pihak ini.',
      outstandingAmt: 'Tertunggak',
      noDueDate: 'Tiada tarikh matang',
    },
    toast: {
      allocationApplied: 'Peruntukan berjaya',
      allocationReversed: 'Peruntukan disongsangkan',
      noUnallocated: 'Tiada baki untuk diperuntukkan',
      loadFailed: 'Gagal memuat {{entity}}',
      allocationFailed: 'Gagal memperuntukkan',
      reverseFailed: 'Gagal menyongsangkan',
      voidConfirm: 'Batal {{kind}} ini? Peruntukan sedia ada akan disongsangkan.',
      voided: '{{title}} dibatalkan',
      voidFailed: 'Gagal membatalkan {{entity}}',
    },
    empty: {
      selectRecord: 'Pilih rekod untuk periksa kesediaan peruntukan dan dokumen rakan niaga tertunggak.',
    },
  },
} as const;

export default ms;
